/* tslint:disable */
/* eslint-disable */
/**
 * Scrivio Zorgtrajecten API
 * API for managing zorgtrajecten (care trajectories) within an organization. A zorgtraject is the core container for treatment in the ZPM (Zorgprestatiemodel) billing model. Everything flows from client → zorgtraject → afspraken → declaraties. All operations are scoped to the authenticated user\'s organization. 
 *
 * The version of the OpenAPI document: 1.0.0
 * 
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import type { Configuration } from './configuration';
import type { AxiosPromise, AxiosInstance, RawAxiosRequestConfig } from 'axios';
import globalAxios from 'axios';
// Some imports not used depending on template conditions
// @ts-ignore
import { DUMMY_BASE_URL, assertParamExists, setApiKeyToObject, setBasicAuthToObject, setBearerAuthToObject, setOAuthToObject, setSearchParams, serializeDataIfNeeded, toPathString, createRequestFunction } from './common';
import type { RequestArgs } from './base';
// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS, BaseAPI, RequiredError, operationServerMap } from './base';

/**
 * Basis GGZ behandelprofiel
 */

export const BasisGgzProfiel = {
    Kort: 'KORT',
    Middel: 'MIDDEL',
    Intensief: 'INTENSIEF',
    Chronisch: 'CHRONISCH'
} as const;

export type BasisGgzProfiel = typeof BasisGgzProfiel[keyof typeof BasisGgzProfiel];


/**
 * Status of behandelconsult prerequisites for a zorgtraject. A behandelconsult (treatment appointment) requires: 1. A DSM-5 diagnosis set on the zorgtraject 2. At least one completed diagnostic appointment with the regiebehandelaar 
 */
export interface BehandelconsultPrerequisiteStatus {
    /**
     * Whether all prerequisites are met and a behandelconsult can be created
     */
    'canCreateBehandelconsult': boolean;
    /**
     * Whether a DSM-5 diagnosis exists on the zorgtraject
     */
    'hasDsm5Diagnosis': boolean;
    /**
     * The DSM-5 diagnosis text (if present)
     */
    'dsm5Diagnosis'?: string | null;
    /**
     * Whether a completed diagnostic appointment with regiebehandelaar exists
     */
    'hasCompletedDiagnosticAppointment': boolean;
    /**
     * Date of the first completed diagnostic appointment (formatted as \"d MMM yyyy\")
     */
    'diagnosticAppointmentDate'?: string | null;
    /**
     * Name of the regiebehandelaar who conducted the diagnostic appointment
     */
    'diagnosticAppointmentBehandelaar'?: string | null;
    /**
     * Name of the regiebehandelaar assigned to the client
     */
    'regiebehandelaarName'?: string | null;
    /**
     * Human-readable message explaining the status
     */
    'message': string;
    /**
     * List of missing prerequisites (for UI display). Empty when canCreateBehandelconsult is true. 
     */
    'missingPrerequisites': Array<string>;
}
/**
 * Care intensity level for residential stays (verblijfsdagen) in the ZPM. Required for clients who will be billed for residential (VD) service codes: - A: Lichte verzorgingsgraad (VD0001) - B: Beperkte verzorgingsgraad (VD0009) - C: Matige verzorgingsgraad (VD0017) - D: Gemiddelde verzorgingsgraad (VD0025) - E: Intensieve verzorgingsgraad (VD0033) 
 */

export const CareIntensity = {
    A: 'A',
    B: 'B',
    C: 'C',
    D: 'D',
    E: 'E'
} as const;

export type CareIntensity = typeof CareIntensity[keyof typeof CareIntensity];


export interface CloseZorgtrajectRequest {
    /**
     * End date for closing the zorgtraject
     */
    'einddatum': string;
    /**
     * On a Jeugdwet zorgtraject, the toewijzingen whose stop message (JW307) is to be queued now, each with its stop date and reden. Every other toewijzing still owed one is deferred. Each must be a toewijzing of this zorgtraject whose `stopbericht` is owed. 
     */
    'stopberichten'?: Array<ToewijzingStopberichtKeuze>;
}
export interface CreateZorgtrajectRequest {
    /**
     * Optional descriptive name/title for the zorgtraject
     */
    'name'?: string | null;
    /**
     * Optional zorgtrajectnummer to preserve verbatim (imports/migrations that already carry a Vecozo number). When supplied it must be a valid random UUID version 4 (a non-UUID value returns 422). When omitted or blank, a UUID version 4 is generated automatically. Must be unique within the organization; a duplicate returns 409. 
     */
    'zorgtrajectNummer'?: string | null;
    /**
     * Reference to the location for billing purposes. If null, the organization\'s default AGB code is used. 
     */
    'locationId'?: string | null;
    /**
     * Which financing regime (wettelijk kader) funds this zorgtraject. It decides which of the server\'s rules the zorgtraject is held to, and it is fixed once created: a different regime is a new zorgtraject.  Omitted, the zorgtraject records no regime and is validated exactly as a `ZVW` one (#4698). 
     */
    'financingRegime'?: FinancingRegime;
    /**
     * Behandelsetting. Required for a `ZVW` zorgtraject, and for one that states no `financingRegime`; the server enforces it with a `400` carrying field-level Problem Details, because OpenAPI 3.0 cannot express the conditional (#4698). 
     */
    'setting'?: Setting;
    /**
     * Start date of the zorgtraject
     */
    'startdatum': string;
    /**
     * End date (usually not set on creation)
     */
    'einddatum'?: string | null;
    /**
     * User ID of the regiebehandelaar. Required for a `ZVW` zorgtraject, and for one that states no `financingRegime`, where it must also be a user the organization has designated as regiebehandelaar (#3115). The server enforces the requirement with a `400` carrying field-level Problem Details (#4698). 
     */
    'regiebehandelaarId'?: string;
    /**
     * User ID of the primair behandelaar (dossierverantwoordelijke). Optional — when supplied, the user must be a member of the client\'s behandelteam. 
     */
    'primairBehandelaarId'?: string | null;
    /**
     * Zorgvraagtypering code (ZT01-ZT21). Optional at creation time — the typering is typically determined after intake (e.g. via a HoNOS+ assessment) and can be set via the herijking endpoint. 
     */
    'zorgvraagtyperingCode'?: ZorgvraagtyperingCode | null;
    /**
     * DSM hoofdgroep per Vektis CL0005-NZA. Optional at creation time — it is typically determined after intake, and can be recorded later via `POST /zorgtrajecten/{id}/ggz-profiel/wijzigen`. When supplied it must be valid on the zorgtraject\'s startdatum.  Refused with a `400` when `ggzType` is `BASIS_GGZ`, and when a `basisGgzProfiel` is supplied alongside it: a zorgtraject records the diagnosis of one kind of ggz, never both (#3594).  Never derived from `dsm5Diagnoses` (#3522): the hoofdgroep is recorded by the practice, and omitting it here leaves the zorgtraject without one. The diagnosis does *propose* a value — read `voorgesteldeDsmHoofdgroep` off the zorgtraject and offer it for confirmation — but nothing applies that proposal (#4351). 
     */
    'dsmHoofdgroepCode'?: string | null;
    /**
     * Structured DSM-5 diagnoses from the official NZa codelist, each with its own differentiaal/definitief status. 
     */
    'dsm5Diagnoses'?: Array<Dsm5DiagnosisSelection> | null;
    /**
     * Which of `dsm5Diagnoses` is the primary diagnosis — the one letters name when they can only name one. Must be one of their ids; omit it when the primary is not (yet) determined. 
     */
    'primairDsm5DiagnosisId'?: string | null;
    /**
     * Which kind of ggz this zorgtraject delivers. Optional: a trajectory may be opened before the practice has decided, and an untyped trajectory simply declares no diagnosecodelijst. Recording it later is what the declaration needs (#3518, #3533).  May be omitted when a diagnosis is given: a GB-ggz profiel can only mean basis ggz and a DSM hoofdgroep only gespecialiseerde ggz, so the backend derives the type from the diagnosis rather than taking a second answer (#3594). Type and diagnosis are stored together as the trajectory\'s first GGZ-profiel period.  `GESPECIALISEERDE_GGZ` together with a `basisGgzProfiel` is refused with a `400` carrying field-level Problem Details — the GB-ggz profiel is the basis-ggz diagnosis and means nothing on a gespecialiseerd trajectory. OpenAPI 3.0 cannot express that conditional, so the backend enforces it. 
     */
    'ggzType'?: GgzType | null;
    /**
     * GB-ggz behandelprofiel, the diagnosis a basis-ggz declaration carries (codelijst `033`). Refused when `ggzType` is `GESPECIALISEERDE_GGZ`, and when a `dsmHoofdgroepCode` is supplied alongside it (#3594). 
     */
    'basisGgzProfiel'?: BasisGgzProfiel | null;
    /**
     * Care intensity level for residential billing (verblijfsdagen). Set when the client has VERBLIJF appointment types. 
     */
    'careIntensity'?: CareIntensity | null;
    /**
     * Whether a referral (verwijzing) is present.  When `true`, `verwijzerType`, `verwijzerNaam` and `verwijsdatum` are required, and so is `agbVerwijzer` unless `verwijstype` is `VERWIJZING_AANWEZIG_ZONDER_AGB` (Vektis CL0004-NZA 07). OpenAPI 3.0 cannot express that conditional, so the backend enforces it and answers a violation with a `400` carrying field-level Problem Details. 
     */
    'verwijzingAanwezig': boolean;
    /**
     * Type of referrer. Required when `verwijzingAanwezig` is true.
     */
    'verwijzerType'?: VerwijzerType | null;
    /**
     * Type verwijzing per Vektis CL0004-NZA, shown on de restitutienota
     */
    'verwijstype'?: VerwijstypeCode | null;
    /**
     * AGB code of the verwijzer. Required when `verwijzingAanwezig` is true, unless `verwijstype` is `VERWIJZING_AANWEZIG_ZONDER_AGB` (Vektis CL0004-NZA 07) — the code that declares this verwijzer has no AGB-code. 
     */
    'agbVerwijzer'?: string | null;
    /**
     * Date of the referral (verwijsdatum) for GDS801 XML generation. Required when `verwijzingAanwezig` is true. 
     */
    'verwijsdatum'?: string | null;
    /**
     * Link to verwijsbrief document
     */
    'verwijsbriefDocumentId'?: string | null;
    /**
     * Name of the referrer (e.g., initials + surname). Required when `verwijzingAanwezig` is true. 
     */
    'verwijzerNaam'?: string | null;
    /**
     * Email address of the referrer, used for sending letters via Zivver
     */
    'verwijzerEmail'?: string | null;
}


/**
 * Status of a DSM-5 diagnose: - DIFFERENTIAAL: Differential diagnosis (working hypothesis) - DEFINITIEF: Definitive diagnosis (confirmed) 
 */

export const DiagnoseStatus = {
    Differentiaal: 'DIFFERENTIAAL',
    Definitief: 'DEFINITIEF'
} as const;

export type DiagnoseStatus = typeof DiagnoseStatus[keyof typeof DiagnoseStatus];


/**
 * One structured DSM-5 diagnosis a zorgtraject should carry, with the differentiaal/definitief status that applies to that code. 
 */
export interface Dsm5DiagnosisSelection {
    /**
     * The DSM-5 diagnosis, from the official NZa codelist
     */
    'id': string;
    /**
     * Whether this diagnosis is differentiaal or definitief. Omit it — or send null — when the distinction has not been made for this code; what is sent is what is stored, so omitting it on a code that has a status clears that status. There is deliberately no `default:` (#3213). 
     */
    'status'?: DiagnoseStatus | null;
}


/**
 * Summary of a DSM-5 diagnosis for inclusion in zorgtraject responses
 */
export interface Dsm5DiagnosisSummary {
    /**
     * Unique identifier
     */
    'id': string;
    /**
     * DSM-5 diagnosis code (e.g., \"D5_1.01.01\")
     */
    'code': string;
    /**
     * Full description of the diagnosis
     */
    'description': string;
    /**
     * ICD-10 code for GDS801 billing compatibility
     */
    'icd10Code'?: string | null;
    /**
     * Whether this is the zorgtraject\'s primary diagnosis. At most one entry per zorgtraject is primary; none is when it has not been determined. 
     */
    'isPrimair': boolean;
    /**
     * Whether *this* diagnosis is differentiaal or definitief. Null when never recorded. Stated per diagnosis rather than once for the zorgtraject. 
     */
    'status'?: DiagnoseStatus | null;
}


/**
 * Financing regime (wettelijk kader) under which care is funded and declared
 */

export const FinancingRegime = {
    Zvw: 'ZVW',
    Wmo: 'WMO',
    Jeugdwet: 'JEUGDWET',
    Particulier: 'PARTICULIER',
    Pgb: 'PGB',
    Anders: 'ANDERS'
} as const;

export type FinancingRegime = typeof FinancingRegime[keyof typeof FinancingRegime];


/**
 * Which kind of ggz a zorgtraject delivers. Chosen per zorgtraject — a practice delivers both — and it decides which diagnosis codelist the declaration carries (Vektis `DiagnoseCodelijstCode`): - BASIS_GGZ: codelijst `033`, the GB-ggz profiel (CL0009-NZA: `GBG01` kort,   `GBG02` middel, `GBG03` intensief, `GBG04` chronisch) - GESPECIALISEERDE_GGZ: codelijst `029`, the DSM hoofdgroep (CL0005-NZA, `01`-`23`)  Absent on a trajectory whose type was never recorded. The codelist is selected from this value alone and never inferred from which diagnosis field happens to be filled, so an untyped trajectory declares no codelist at all. 
 */

export const GgzType = {
    BasisGgz: 'BASIS_GGZ',
    GespecialiseerdeGgz: 'GESPECIALISEERDE_GGZ'
} as const;

export type GgzType = typeof GgzType[keyof typeof GgzType];


/**
 * iJw `LDT_ProductCategorie` (basisschema iJw 3.2): the productcategorieën a Jeugdwet toewijzing may name. Mirrors `src/main/resources/xsd/istandaarden/ijw/basisschema.xsd`; `IstandaardCodelijstParityTest` fails the build when the two differ, so a new iJw release that changes the list is a change here too (#4839). 
 */

export const IjwProductCategorie = {
    _31: '31',
    _32: '32',
    _33: '33',
    _34: '34',
    _35: '35',
    _36: '36',
    _37: '37',
    _38: '38',
    _40: '40',
    _41: '41',
    _42: '42',
    _43: '43',
    _44: '44',
    _45: '45',
    _46: '46',
    _47: '47',
    _48: '48',
    _49: '49',
    _50: '50',
    _51: '51',
    _52: '52',
    _53: '53',
    _54: '54',
    _55: '55'
} as const;

export type IjwProductCategorie = typeof IjwProductCategorie[keyof typeof IjwProductCategorie];


/**
 * iJw `LDT_RedenBeeindiging`, the reason on a JW307: OVERLIJDEN (02), TIJDELIJK_BEEINDIGD (20), VOLGENS_PLAN (31), VOORTIJDIG_DOOR_CLIENT (32), VOORTIJDIG_DOOR_AANBIEDER (33), VOORTIJDIG_IN_OVEREENSTEMMING (34), VOORTIJDIG_EXTERNE_OMSTANDIGHEDEN (35), GEINITIEERD_DOOR_GEMEENTE (36), WIJZIGINGSVERZOEK (37), OVERSTAP_ANDERE_AANBIEDER (38), UITSTROOM_ANDER_DOMEIN (39) 
 */

export const IjwRedenBeeindiging = {
    Overlijden: 'OVERLIJDEN',
    TijdelijkBeeindigd: 'TIJDELIJK_BEEINDIGD',
    VolgensPlan: 'VOLGENS_PLAN',
    VoortijdigDoorClient: 'VOORTIJDIG_DOOR_CLIENT',
    VoortijdigDoorAanbieder: 'VOORTIJDIG_DOOR_AANBIEDER',
    VoortijdigInOvereenstemming: 'VOORTIJDIG_IN_OVEREENSTEMMING',
    VoortijdigExterneOmstandigheden: 'VOORTIJDIG_EXTERNE_OMSTANDIGHEDEN',
    GeinitieerdDoorGemeente: 'GEINITIEERD_DOOR_GEMEENTE',
    Wijzigingsverzoek: 'WIJZIGINGSVERZOEK',
    OverstapAndereAanbieder: 'OVERSTAP_ANDERE_AANBIEDER',
    UitstroomAnderDomein: 'UITSTROOM_ANDER_DOMEIN'
} as const;

export type IjwRedenBeeindiging = typeof IjwRedenBeeindiging[keyof typeof IjwRedenBeeindiging];


/**
 * The toewijzing one practitioner\'s session with the cliënt of a zorgtraject went to
 */
export interface KiesToewijzingRequest {
    'zorgtrajectId': string;
    'medewerkerId': string;
    /**
     * One of the session\'s `opties`
     */
    'toewijzingId': string;
}
/**
 * RFC 7807 Problem Details for HTTP APIs
 */
export interface ProblemDetail {
    /**
     * URI reference identifying the problem type
     */
    'type': string;
    /**
     * Short, human-readable summary
     */
    'title': string;
    /**
     * HTTP status code
     */
    'status': number;
    /**
     * Human-readable explanation
     */
    'detail'?: string;
    /**
     * URI reference identifying the specific occurrence
     */
    'instance'?: string;
    /**
     * List of validation errors
     */
    'errors'?: Array<ProblemDetailErrorsInner>;
}
export interface ProblemDetailErrorsInner {
    'field'?: string;
    'message'?: string;
}
/**
 * Care a medewerker delivered on a zorgtraject (ADR-030 addendum A4). Sent whole on both create and update.  With an `appointmentId`, the direct time and the date are the appointment\'s: send `directMinutes` and `datum` equal to them, or leave `directMinutes` out. Registered time and the agenda cannot diverge — writing more minutes means making the appointment longer. Without one, `directMinutes` is required. OpenAPI 3.0 cannot express either, so the server enforces them with a `400` naming the field. 
 */
export interface RegistratieRequest {
    /**
     * Who delivered the care; a member of the organisation
     */
    'medewerkerId': string;
    /**
     * The day the care was delivered
     */
    'datum': string;
    /**
     * Direct time with or for the cliënt. Required without an appointment.
     */
    'directMinutes'?: number | null;
    /**
     * Indirect time — overleg, reporting. Counts toward delivered volume under the Jeugdwet.
     */
    'indirectMinutes': number;
    'travelMinutes': number;
    /**
     * The toewijzing delivered against. Required on a `JEUGDWET` zorgtraject; when set it must belong to this zorgtraject and name the medewerker. 
     */
    'toewijzingId'?: string | null;
    /**
     * The appointment the care was delivered in, which must be one with the zorgtraject\'s cliënt
     */
    'appointmentId'?: string | null;
    /**
     * What was done, in the medewerker\'s words
     */
    'omschrijving'?: string | null;
}
/**
 * One registration of delivered care (ADR-030 addendum A4)
 */
export interface RegistratieResponse {
    'id': string;
    'zorgtrajectId': string;
    'medewerkerId': string;
    'datum': string;
    /**
     * The appointment\'s duration when `appointmentId` is set, the registered figure otherwise
     */
    'directMinutes': number;
    'indirectMinutes': number;
    'travelMinutes': number;
    'toewijzingId'?: string | null;
    'appointmentId'?: string | null;
    'omschrijving'?: string | null;
    /**
     * Epoch milliseconds
     */
    'createdAt': number;
}
/**
 * Treatment setting (behandelsetting) per NZA tariff structure: - AMBULANT_MONO: Ambulant monodisciplinair (outpatient monodisciplinary) - AMBULANT_MULTI: Ambulant multidisciplinair (outpatient multidisciplinary) - KLINISCH: Klinisch (inpatient clinical) - PRAKTIJK: Praktijk (private practice) 
 */

export const Setting = {
    AmbulantMono: 'AMBULANT_MONO',
    AmbulantMulti: 'AMBULANT_MULTI',
    Klinisch: 'KLINISCH',
    Praktijk: 'PRAKTIJK'
} as const;

export type Setting = typeof Setting[keyof typeof Setting];


/**
 * A change the gemeente approved without a message (ADR-030 V1): a new `volume`, a new `einddatum`, or both. At least one must differ from what applies now; OpenAPI 3.0 cannot express that, so the server enforces it with a `400` naming both fields. 
 */
export interface ToewijzingAanpassingRequest {
    /**
     * The new volume, in the toewijzing\'s own eenheid and frequentie. Only on a toewijzing that has an `omvang`. 
     */
    'volume'?: number | null;
    /**
     * The new last day the product may be delivered
     */
    'einddatum'?: string | null;
    /**
     * From when the change applies; not before the toewijzing\'s `ingangsdatum`
     */
    'ingangsdatum': string;
    /**
     * Why, in the practice\'s words
     */
    'reden': string;
}
/**
 * One adjustment recorded on a toewijzing (ADR-030 V1)
 */
export interface ToewijzingAanpassingResponse {
    'id': string;
    /**
     * The volume it set; null when it left the volume alone
     */
    'volume'?: number | null;
    /**
     * The einddatum it set; null when it left the einddatum alone
     */
    'einddatum'?: string | null;
    'ingangsdatum': string;
    'reden': string;
    /**
     * Whether a later allocation superseded it — a JW301 for the same `toewijzingNummer`, or on a `MANUAL` toewijzing an `updateToewijzing` that changed what was allocated. The gemeente has spoken again, and it no longer counts towards what applies 
     */
    'vervallen': boolean;
    'createdBy': string;
    /**
     * Epoch milliseconds
     */
    'createdAt': number;
}
/**
 * An outgoing start or stop message of a toewijzing and how far it got
 */
export interface ToewijzingBerichtstand {
    /**
     * The bericht in the Berichten outbox
     */
    'berichtId': string;
    'status': ToewijzingBerichtstandStatus;
    /**
     * When it was queued in the outbox
     */
    'klaargezetAt': number;
    /**
     * When it was sent to VECOZO; null while it waits in the outbox
     */
    'verzondenAt'?: number | null;
    /**
     * When the gemeente\'s retour on it arrived; null while none has
     */
    'beantwoordAt'?: number | null;
}


/**
 * Where an outgoing start or stop message stands. - `KLAARGEZET` — in the outbox, not yet sent - `VERZONDEN` — sent; the gemeente\'s retour has not come back - `GEACCEPTEERD` — the gemeente\'s retour accepted it - `AFGEKEURD` — VECOZO or the gemeente\'s retour rejected it 
 */

export const ToewijzingBerichtstandStatus = {
    Klaargezet: 'KLAARGEZET',
    Verzonden: 'VERZONDEN',
    Geaccepteerd: 'GEACCEPTEERD',
    Afgekeurd: 'AFGEKEURD'
} as const;

export type ToewijzingBerichtstandStatus = typeof ToewijzingBerichtstandStatus[keyof typeof ToewijzingBerichtstandStatus];


/**
 * iJw `LDT_Eenheid`: MINUUT (01), UUR (04), ETMAAL (14), DAGDEEL (16, four hours), STUKS_OUTPUT (82), EURO (83), STUKS_INSPANNING (84) 
 */

export const ToewijzingEenheid = {
    Minuut: 'MINUUT',
    Uur: 'UUR',
    Etmaal: 'ETMAAL',
    Dagdeel: 'DAGDEEL',
    StuksOutput: 'STUKS_OUTPUT',
    Euro: 'EURO',
    StuksInspanning: 'STUKS_INSPANNING'
} as const;

export type ToewijzingEenheid = typeof ToewijzingEenheid[keyof typeof ToewijzingEenheid];


/**
 * iJw `LDT_Frequentie`: PER_DAG (1), PER_WEEK (2), PER_MAAND (4), TOTAAL (6, within the toewijzing\'s period) 
 */

export const ToewijzingFrequentie = {
    PerDag: 'PER_DAG',
    PerWeek: 'PER_WEEK',
    PerMaand: 'PER_MAAND',
    Totaal: 'TOTAAL'
} as const;

export type ToewijzingFrequentie = typeof ToewijzingFrequentie[keyof typeof ToewijzingFrequentie];


/**
 * A toewijzing a session may go to
 */
export interface ToewijzingKeuzeOptie {
    'toewijzingId': string;
    'toewijzingNummer': number;
    'productCategorie'?: string | null;
    'productCode'?: string | null;
    'ingangsdatum': string;
}
/**
 * One practitioner\'s session with one cliënt on an afgeronde afspraak, on a Jeugdwet zorgtraject (#4819)
 */
export interface ToewijzingKeuzeResponse {
    'zorgtrajectId': string;
    'clientId': string;
    'medewerkerId': string;
    /**
     * The registration the session became; absent while nothing is registered
     */
    'registratieId'?: string | null;
    /**
     * The toewijzing the registration is on
     */
    'toewijzingId'?: string | null;
    /**
     * More than one toewijzing covers the session and nobody has chosen yet
     */
    'open': boolean;
    /**
     * The toewijzingen that name the practitioner and cover the session\'s date
     */
    'opties': Array<ToewijzingKeuzeOptie>;
}
/**
 * iJw `CDT_Omvang` — a volume in an eenheid, per a frequentie, always all three. A toewijzing carries one exactly when it names a `productCode`, and never with a `budget`; absent otherwise. 
 */
export interface ToewijzingOmvang {
    'volume': number;
    'eenheid': ToewijzingEenheid;
    'frequentie': ToewijzingFrequentie;
}


/**
 * A contracted product an aspecifieke toewijzing can be declared as
 */
export interface ToewijzingProductKandidaat {
    'productCode': string;
    /**
     * The contract\'s description of the product, where it has one
     */
    'omschrijving'?: string | null;
}
/**
 * The product an aspecifieke toewijzing is declared as; null or absent clears the choice
 */
export interface ToewijzingProductKeuzeRequest {
    /**
     * One of the toewijzing\'s `productKandidaten`; null or absent clears the choice
     */
    'productCode'?: string | null;
}
/**
 * iJw `LDT_RedenWijziging`, in code order 01–12: administratieve correctie, cliënt overleden, contractwijziging, herbeoordeling verlenging, herbeoordeling verkorting, uitstroom naar ander domein, verhuizing naar andere gemeente, wijziging leveringsvorm, overstap naar andere aanbieder, nieuwe bekostigingssystematiek, gemeentelijke herindeling, geïnitieerd door de aanbieder 
 */

export const ToewijzingRedenWijziging = {
    AdministratieveCorrectie: 'ADMINISTRATIEVE_CORRECTIE',
    ClientOverleden: 'CLIENT_OVERLEDEN',
    Contractwijziging: 'CONTRACTWIJZIGING',
    Verlenging: 'VERLENGING',
    Verkorting: 'VERKORTING',
    UitstroomAnderDomein: 'UITSTROOM_ANDER_DOMEIN',
    Verhuizing: 'VERHUIZING',
    WijzigingLeveringsvorm: 'WIJZIGING_LEVERINGSVORM',
    OverstapAndereAanbieder: 'OVERSTAP_ANDERE_AANBIEDER',
    NieuweBekostigingssystematiek: 'NIEUWE_BEKOSTIGINGSSYSTEMATIEK',
    GemeentelijkeHerindeling: 'GEMEENTELIJKE_HERINDELING',
    GeinitieerdDoorAanbieder: 'GEINITIEERD_DOOR_AANBIEDER'
} as const;

export type ToewijzingRedenWijziging = typeof ToewijzingRedenWijziging[keyof typeof ToewijzingRedenWijziging];


/**
 * Everything a toewijzing says — one toegewezen product, shaped by JW301 `ToegewezenProduct` (ADR-030 addendum A1). Sent whole on both create and update.  What it says about its product, `omvang` and `budget` takes one of three shapes (iJw 3.2 CD041, CD077, CD078, CD080, OP302):  - **specifiek** — a `productCode` (with its `productCategorie`) and an `omvang`, no `budget`; - **aspecifiek** — a `productCategorie` only, no `omvang`, and an optional indicatief `budget`; - **generiek** — no product at all, and a `budget`.  So `omvang` and `budget` are never both set, and an `omvang` is set exactly when a `productCode` is. OpenAPI 3.0 cannot express that, so the server enforces it with a `400` naming the offending fields.  The bounds below are the iJw XSD\'s, and the toewijzing form shows them before submit from a copy in `scrivio-fe/src/composables/jeugdwet/toewijzingen.ts` (`TOEWIJZING_GRENZEN`): the generated client carries no validation keywords. Change both together (#4839). 
 */
export interface ToewijzingRequest {
    /**
     * The gemeente\'s number for this toewijzing (`LDT_Nummer`). Unique per organisation and gemeente; a number already held is refused with `409`. 
     */
    'toewijzingNummer': number;
    /**
     * CBS gemeentecode of the allocating gemeente; must name a known gemeente
     */
    'gemeentecode': string;
    /**
     * Productcategorie (`CDT_Product.Categorie`). Required with a `productCode`; null on a generieke toewijzing, which names no product.  Must be in the zorgtraject\'s regime\'s `LDT_ProductCategorie` — on a Jeugdwet zorgtraject one of `IjwProductCategorie`, on a Wmo one the iWmo list (01..18) — or the server answers `400` naming `productCategorie`. Not an `enum` here because the list depends on the zorgtraject, which the request does not carry (#4839). 
     */
    'productCategorie'?: string | null;
    /**
     * Productcode (`CDT_Product.Code`). Not constrained to the national standaardproductcodelijst: regions define codes of their own. Null on an aspecifieke toewijzing, which names only the categorie, and on a generieke one. Set exactly when `omvang` is. 
     */
    'productCode'?: string | null;
    /**
     * The date the gemeente decided the toewijzing
     */
    'toewijzingsdatum': string;
    /**
     * The first day the product may be delivered
     */
    'ingangsdatum': string;
    /**
     * The last day the product may be delivered; not before `ingangsdatum`
     */
    'einddatum'?: string | null;
    'omvang'?: ToewijzingOmvang;
    /**
     * The allocated budget in eurocents (`LDT_Budget`). Never together with `omvang`; optional on an aspecifieke toewijzing, where it is indicatief. 
     */
    'budget'?: number | null;
    'redenWijziging'?: ToewijzingRedenWijziging | null;
    'status': ToewijzingStatus;
    /**
     * The medewerkers who deliver against this toewijzing; each must be a member of the organisation. May be empty; a repeated id counts once. Deliberately not `uniqueItems`: the TypeScript generator types that as a `Set`, which serialises to `{}`, so the frontend could not send it. 
     */
    'medewerkerIds': Array<string>;
}


/**
 * One toewijzing — a gemeente\'s allocation of one product (ADR-030 addendum A1)
 */
export interface ToewijzingResponse {
    'id': string;
    'zorgtrajectId': string;
    'toewijzingNummer': number;
    'gemeentecode': string;
    /**
     * Null on a generieke toewijzing
     */
    'productCategorie'?: string | null;
    /**
     * The gemeente\'s productcode. Null on an aspecifieke and a generieke toewijzing
     */
    'productCode'?: string | null;
    /**
     * The product the practice chose to declare an aspecifieke toewijzing as (`chooseToewijzingProduct`). Null on any other toewijzing, and until one is chosen. 
     */
    'gekozenProductCode'?: string | null;
    /**
     * The products an aspecifieke toewijzing can be declared as: those the Jeugdwet contracts with its gemeente hold in its `productCategorie` on its `ingangsdatum`, by productcode. Empty on any other toewijzing. 
     */
    'productKandidaten': Array<ToewijzingProductKandidaat>;
    'toewijzingsdatum': string;
    'ingangsdatum': string;
    'einddatum'?: string | null;
    'omvang'?: ToewijzingOmvang;
    /**
     * Eurocents. Never together with `omvang`
     */
    'budget'?: number | null;
    'redenWijziging'?: ToewijzingRedenWijziging | null;
    'status': ToewijzingStatus;
    'source': ToewijzingSource;
    'medewerkerIds': Array<string>;
    'createdAt': number;
    /**
     * The `omvang` as it applies now: its volume raised or lowered by the most recent adjustment that sets one, its eenheid and frequentie the gemeente\'s. Absent exactly when `omvang` is. Utilisation is measured against this, not against `omvang`. 
     */
    'geldendeOmvang'?: ToewijzingOmvang;
    /**
     * The einddatum as it applies now — the most recent adjustment\'s that sets one, the stored `einddatum` otherwise. 
     */
    'geldendeEinddatum'?: string | null;
    /**
     * How much of the toewijzing is used up, from the registraties against it (`listRegistraties`). Present exactly when `geldendeOmvang` is a total over the period (frequentie TOTAAL) in an eenheid of time (MINUUT or UUR); absent otherwise. 
     */
    'uitnutting'?: ToewijzingUitnutting;
    /**
     * Every adjustment recorded on this toewijzing, most recent first, superseded ones included (ADR-030 V1). `omvang` and `einddatum` above stay what was received. 
     */
    'aanpassingen': Array<ToewijzingAanpassingResponse>;
    /**
     * Where the stop message (JW307) stands. Null until someone was asked; a stop message is owed while this is not KLAARGEZET and `status` is not BEEINDIGD. 
     */
    'stopbericht'?: ToewijzingStopbericht | null;
    /**
     * Whether the start (JW305) was communicated. Null until it is: the first afgeronde afspraak registered against the toewijzing queues it. 
     */
    'startbericht'?: ToewijzingStartbericht | null;
    /**
     * The `Begindatum` the start message stated — the first afspraak\'s date, or the ingangsdatum when that afspraak lies before it. Set exactly when `startbericht` is. 
     */
    'startdatum'?: string | null;
    /**
     * Present when the gemeente\'s JW306 rejected the latest start message (#4866): declaring against the toewijzing is held until a corrected one is sent again and accepted. Absent when no start was queued, it awaits its JW306, or it was accepted. 
     */
    'startberichtAfkeuring'?: ToewijzingStartberichtAfkeuring;
    /**
     * Where the latest start message (JW305) Scrivio queued stands, from the outbox through the gemeente\'s JW306. Absent when `startbericht` is not KLAARGEZET. 
     */
    'startberichtStand'?: ToewijzingBerichtstand;
    /**
     * Where the latest stop message (JW307) Scrivio queued stands, from the outbox through the gemeente\'s JW308. Absent when `stopbericht` is not KLAARGEZET. 
     */
    'stopberichtStand'?: ToewijzingBerichtstand;
    /**
     * The latest request to change this toewijzing (JW317) and how far it got. Absent when none was queued, and once the gemeente has rewritten the toewijzing since — that JW301 is the answer. 
     */
    'wijzigingsverzoek'?: ToewijzingWijzigingsverzoek;
}


/**
 * One retourcode and its meaning
 */
export interface ToewijzingRetourcode {
    'code': string;
    /**
     * What the iStandaard says the code means; absent for a code WJ001 does not list
     */
    'omschrijving'?: string | null;
}
/**
 * Who wrote the toewijzing: - MANUAL: entered by hand; the practice may change and remove it - JW301: the gemeente\'s message; read-only through this API - IMPORT: migrated from the practice\'s previous EPD; read-only through this API, and taken   over by a later JW301 for the same number 
 */

export const ToewijzingSource = {
    Manual: 'MANUAL',
    Jw301: 'JW301',
    Import: 'IMPORT'
} as const;

export type ToewijzingSource = typeof ToewijzingSource[keyof typeof ToewijzingSource];


/**
 * Whether a toewijzing\'s start message (JW305) was communicated (#4815): - KLAARGEZET: the JW305 is queued in the outbox; the gemeente\'s JW306 says whether it is accepted - ELDERS_GECOMMUNICEERD: the start was communicated before Scrivio, by the practice\'s previous system 
 */

export const ToewijzingStartbericht = {
    Klaargezet: 'KLAARGEZET',
    EldersGecommuniceerd: 'ELDERS_GECOMMUNICEERD'
} as const;

export type ToewijzingStartbericht = typeof ToewijzingStartbericht[keyof typeof ToewijzingStartbericht];


/**
 * Why the gemeente rejected a toewijzing\'s start message (JW305), as its JW306 said
 */
export interface ToewijzingStartberichtAfkeuring {
    /**
     * The rejecting retourcodes (codelijst WJ001), in the order received
     */
    'retourcodes': Array<ToewijzingRetourcode>;
}
/**
 * Lifecycle of one toewijzing: - TOEGEWEZEN: allocated, care not started - GESTART: care started - BEEINDIGD: care ended 
 */

export const ToewijzingStatus = {
    Toegewezen: 'TOEGEWEZEN',
    Gestart: 'GESTART',
    Beeindigd: 'BEEINDIGD'
} as const;

export type ToewijzingStatus = typeof ToewijzingStatus[keyof typeof ToewijzingStatus];


/**
 * Where a toewijzing\'s stop message (JW307) stands (#4816): - UITGESTELD: the zorgtraject was closed and the stop deferred; a signal stays until it is queued - KLAARGEZET: the JW307 is queued in the outbox 
 */

export const ToewijzingStopbericht = {
    Uitgesteld: 'UITGESTELD',
    Klaargezet: 'KLAARGEZET'
} as const;

export type ToewijzingStopbericht = typeof ToewijzingStopbericht[keyof typeof ToewijzingStopbericht];


/**
 * One toewijzing whose stop message is sent on closing (#4816)
 */
export interface ToewijzingStopberichtKeuze {
    'toewijzingId': string;
    /**
     * The last day delivered; not before the toewijzing\'s `ingangsdatum`
     */
    'einddatum': string;
    'redenBeeindiging': IjwRedenBeeindiging;
}


/**
 * A toewijzing\'s stop message (JW307), queued from the toewijzing (#4816)
 */
export interface ToewijzingStopberichtRequest {
    /**
     * The last day delivered; not before the toewijzing\'s `ingangsdatum`
     */
    'einddatum': string;
    'redenBeeindiging': IjwRedenBeeindiging;
}


/**
 * How much of a toewijzing is used up, measured against its `geldendeOmvang`
 */
export interface ToewijzingUitnutting {
    /**
     * The direct, indirect and travel time registered against the toewijzing, in the omvang\'s eenheid — 90 minutes against an omvang in UUR is 1.5 
     */
    'verbruikt': number;
    /**
     * The share of the applying volume registered, 0 to 100 and beyond; 0 when the volume is 0
     */
    'percentage': number;
}
/**
 * A JW317 asking the gemeente to change a toewijzing, and how far it got
 */
export interface ToewijzingWijzigingsverzoek {
    /**
     * The JW317 in the Berichten outbox
     */
    'berichtId': string;
    'dagtekening': string;
    'status': ToewijzingWijzigingsverzoekStatus;
    /**
     * The rejecting retourcodes of the JW318 that rejected it; empty otherwise
     */
    'retourcodes': Array<ToewijzingRetourcode>;
}


/**
 * Where a JW317 stands. - `KLAARGEZET` — in the outbox, not yet sent - `VERZONDEN` — sent; the gemeente\'s JW318 has not come back - `ONTVANGEN` — the JW318 accepted it; the gemeente decides with a JW301 or a JW319 - `AFGEKEURD` — VECOZO or the gemeente\'s JW318 rejected it - `IN_ONDERZOEK` — the gemeente\'s JW319 says it is looking into it - `AFGEWEZEN` — the gemeente\'s JW319 turned it down 
 */

export const ToewijzingWijzigingsverzoekStatus = {
    Klaargezet: 'KLAARGEZET',
    Verzonden: 'VERZONDEN',
    Ontvangen: 'ONTVANGEN',
    Afgekeurd: 'AFGEKEURD',
    InOnderzoek: 'IN_ONDERZOEK',
    Afgewezen: 'AFGEWEZEN'
} as const;

export type ToewijzingWijzigingsverzoekStatus = typeof ToewijzingWijzigingsverzoekStatus[keyof typeof ToewijzingWijzigingsverzoekStatus];


/**
 * All fields are optional. Only provided fields will be updated. Note: Setting, regiebehandelaar, primair behandelaar, DSM-5 diagnose, basis GGZ-profiel, and zorgvraagtypering are managed via their own versioned endpoints (wijzigen). 
 */
export interface UpdateZorgtrajectRequest {
    /**
     * Never changes the zorgtraject\'s regime: a regime change is a new zorgtraject. Accepted only when it restates the regime the zorgtraject already has (a regime-less zorgtraject counts as `ZVW`); any other value is refused with a `400` carrying a field error on `financingRegime` (#4698). 
     */
    'financingRegime'?: FinancingRegime;
    /**
     * Optional descriptive name/title for the zorgtraject
     */
    'name'?: string | null;
    /**
     * Start date of the zorgtraject (required, cannot be null)
     */
    'startdatum'?: string;
    /**
     * End date of the zorgtraject
     */
    'einddatum'?: string | null;
    /**
     * Whether a referral (verwijzing) is present.  **When this update states the verwijzing**, the zorgtraject that results from it must be complete: with the verwijzing present — set here or already stored — `verwijzerType`, `verwijzerNaam` and `verwijsdatum` are required, and so is `agbVerwijzer` unless `verwijstype` is `VERWIJZING_AANWEZIG_ZONDER_AGB`. Omitted properties keep their stored value, so such an update is validated against the merged result. OpenAPI 3.0 cannot express that conditional, so the backend enforces it and answers a violation with a `400` carrying field-level Problem Details.  An update that states **none** of `verwijzingAanwezig`, `verwijzerType`, `verwijzerNaam`, `verwijsdatum`, `agbVerwijzer` or `verwijstype` is not asked the completeness question at all. This is a whole-entity update, so a rename or a DSM-5 edit carries no verwijzer property — and refusing those on a stored verwijzing they do not touch made a zorgtraject with an incomplete legacy verwijzing un-editable in every other field. Such a row is reported to the practice as a blocking signal on the declaratie surface instead, where it can be completed. 
     */
    'verwijzingAanwezig'?: boolean | null;
    /**
     * Type of referrer. Required when this update states the verwijzing and it is present — see `verwijzingAanwezig`. 
     */
    'verwijzerType'?: VerwijzerType | null;
    /**
     * Type verwijzing per Vektis CL0004-NZA, shown on de restitutienota
     */
    'verwijstype'?: VerwijstypeCode | null;
    /**
     * AGB code of the verwijzer. Required when this update states the verwijzing and it is present, unless `verwijstype` is `VERWIJZING_AANWEZIG_ZONDER_AGB` — Vektis CL0004-NZA code 07 is what declares that the verwijzer has no AGB code, and nothing else exempts the row. Its **format** is a property of the column and is checked on every update, whether or not this one states the verwijzing. 
     */
    'agbVerwijzer'?: string | null;
    /**
     * Date of the referral (verwijsdatum) for GDS801 XML generation. Required when this update states the verwijzing and it is present. 
     */
    'verwijsdatum'?: string | null;
    /**
     * Link to verwijsbrief document
     */
    'verwijsbriefDocumentId'?: string | null;
    /**
     * Name of the referrer (e.g., initials + surname). Required when this update states the verwijzing and it is present. 
     */
    'verwijzerNaam'?: string | null;
    /**
     * Email address of the referrer, used for sending letters via Zivver. Deliberately not one of the properties that make an update a statement about the verwijzing: it is not part of the completeness rule, so it cannot complete one either. 
     */
    'verwijzerEmail'?: string | null;
    /**
     * Care intensity level for residential billing (verblijfsdagen). Set when the client has VERBLIJF appointment types. 
     */
    'careIntensity'?: CareIntensity | null;
    /**
     * Structured DSM-5 diagnoses from the official NZa codelist, each with its own differentiaal/definitief status. When provided, replaces all existing structured diagnoses — including their statuses, so a code re-sent without one loses the status it had. Pass empty array to clear all structured diagnoses. 
     */
    'dsm5Diagnoses'?: Array<Dsm5DiagnosisSelection> | null;
    /**
     * Which structured DSM-5 diagnosis is the primary one — the one letters name when they can only name one.  Sent together with `dsm5Diagnoses`, it must be one of their ids and is applied to the replacement set. Omitted while `dsm5Diagnoses` is sent, the current primary is kept if it is still in the new set and dropped otherwise — omitting the field never clears a primary that survives. Sent on its own, it re-prioritises the diagnoses already linked and must be one of them. Explicit `null` clears the primary. 
     */
    'primairDsm5DiagnosisId'?: string | null;
}


/**
 * Type verwijzing per Vektis codelijst CL0004-NZA, required on the restitutienota: - VERWIJZING_AANWEZIG: 01 - Verwijzing aanwezig - DOORVERWIJZING: 02 - Doorverwijzing - GEEN_VERWIJZING_VERLATE_CORRESPONDENTIE: 03 - Geen verwijzing aanwezig vanwege uitzondering, verlate correspondentie - GEEN_VERWIJZING_CORRESPONDENTIE_NIET_TOEGESTAAN: 04 - Geen verwijzing aanwezig vanwege uitzondering, door patiënt geen correspondentie toegestaan - GEEN_VERWIJZING: 05 - Geen verwijzing - GEEN_VERWIJZING_ANDERE_RECHTMATIGHEIDSGROND: 06 - Geen verwijzing, andere rechtmatigheidsgrond - VERWIJZING_AANWEZIG_ZONDER_AGB: 07 - Verwijzing aanwezig, maar verwijzer heeft geen AGB-code 
 */

export const VerwijstypeCode = {
    VerwijzingAanwezig: 'VERWIJZING_AANWEZIG',
    Doorverwijzing: 'DOORVERWIJZING',
    GeenVerwijzingVerlateCorrespondentie: 'GEEN_VERWIJZING_VERLATE_CORRESPONDENTIE',
    GeenVerwijzingCorrespondentieNietToegestaan: 'GEEN_VERWIJZING_CORRESPONDENTIE_NIET_TOEGESTAAN',
    GeenVerwijzing: 'GEEN_VERWIJZING',
    GeenVerwijzingAndereRechtmatigheidsgrond: 'GEEN_VERWIJZING_ANDERE_RECHTMATIGHEIDSGROND',
    VerwijzingAanwezigZonderAgb: 'VERWIJZING_AANWEZIG_ZONDER_AGB'
} as const;

export type VerwijstypeCode = typeof VerwijstypeCode[keyof typeof VerwijstypeCode];


/**
 * Type verwijzer (referrer type): - HUISARTS: Huisarts (General practitioner) - SPECIALIST: Medisch specialist - BEDRIJFSARTS: Bedrijfsarts (Company doctor) - OTHER: Overige verwijzer 
 */

export const VerwijzerType = {
    Huisarts: 'HUISARTS',
    Specialist: 'SPECIALIST',
    Bedrijfsarts: 'BEDRIJFSARTS',
    Other: 'OTHER'
} as const;

export type VerwijzerType = typeof VerwijzerType[keyof typeof VerwijzerType];


/**
 * A DSM hoofdgroep proposed from a zorgtraject\'s recorded DSM-5 diagnosis, together with the diagnosis it was derived from. A **proposal**, never applied: nothing on the server writes it, and it reaches the practitioner as a pre-filled, labelled value in the GGZ-profiel form, which she confirms by saving.  Carries its origin because the practitioner has to be able to judge it: the two taxonomies do not line up one-to-one, and eight of the DSM-5 niveau-1 roots can only fold onto `23 Restgroep`.  Absent from a zorgtraject that records no DSM-5 diagnosis, whose diagnoses point at different hoofdgroepen with none marked primair, or whose diagnosis names a DSM-5 root Scrivio has no answer for. Independent of `dsmHoofdgroepCode`: it is carried whether or not one is already recorded, so the correction dialog can offer it too. 
 */
export interface VoorgesteldeDsmHoofdgroep {
    /**
     * The proposed hoofdgroep, valid on the zorgtraject\'s startdatum
     */
    'code': string;
    /**
     * Betekenis of the proposed hoofdgroep, exactly as CL0005-NZA states it
     */
    'betekenis': string;
    /**
     * The DSM-5 code the proposal was derived from — the primary diagnosis when one is marked, otherwise any of the recorded diagnoses, which all point at this hoofdgroep. 
     */
    'afgeleidVanDsm5Code': string;
    /**
     * Description of that DSM-5 diagnosis
     */
    'afgeleidVanDsm5Omschrijving': string;
}
/**
 * Authorization capabilities for a zorgtraject. Each boolean indicates whether the current user can perform the corresponding action on this zorgtraject. 
 */
export interface ZorgtrajectCapabilitiesResponse {
    /**
     * Whether the user can view the zorgtraject
     */
    'view': boolean;
    /**
     * Whether the user can update the zorgtraject
     */
    'update': boolean;
    /**
     * Whether the user can close the zorgtraject
     */
    'close': boolean;
    /**
     * Whether the user can reopen the zorgtraject
     */
    'reopen': boolean;
    /**
     * Whether the user can change the regiebehandelaar
     */
    'changeRegiebehandelaar': boolean;
    /**
     * Whether the user can change the primair behandelaar (dossierverantwoordelijke) via the versioned endpoint 
     */
    'changePrimairBehandelaar': boolean;
    /**
     * Whether the user can perform zorgvraagtypering reassessment (herijking)
     */
    'performHerijking': boolean;
}
export interface ZorgtrajectListResponse {
    /**
     * List of zorgtrajecten for current page
     */
    'content': Array<ZorgtrajectResponse>;
    /**
     * Current page number (0-indexed)
     */
    'page': number;
    /**
     * Number of items per page
     */
    'size': number;
    /**
     * Total number of zorgtrajecten matching criteria
     */
    'totalElements': number;
    /**
     * Total number of pages
     */
    'totalPages': number;
}
export interface ZorgtrajectMatchResponse {
    /**
     * Whether at least one active zorgtraject was found
     */
    'hasMatch': boolean;
    /**
     * Number of active zorgtrajecten found for this date
     */
    'matchCount': number;
    /**
     * The matched zorgtraject (when exactly one match). Null if no match or multiple matches.
     */
    'zorgtraject'?: ZorgtrajectSummary | null;
    /**
     * All matching zorgtrajecten (when multiple matches). Used for selection UI.
     */
    'zorgtrajecten'?: Array<ZorgtrajectSummary>;
    /**
     * Warning message when no zorgtraject found or ambiguous match
     */
    'warningMessage'?: string | null;
}
export interface ZorgtrajectResponse {
    /**
     * Zorgtraject unique identifier
     */
    'id': string;
    /**
     * Client this zorgtraject belongs to
     */
    'clientId': string;
    /**
     * Client full name
     */
    'clientName'?: string;
    /**
     * Optional descriptive name/title for the zorgtraject
     */
    'name'?: string | null;
    /**
     * Zorgtrajectnummer as a random UUID version 4, as required by the NZa Zorgprestatiemodel (landelijk uniek). Auto-generated on native create, or the UUID supplied verbatim on import. Populates the required GDS801 GGZ Zorgtraject block. 
     */
    'nummer'?: string | null;
    /**
     * Organization this zorgtraject belongs to
     */
    'organizationId': string;
    /**
     * Reference to the location for billing purposes. The location provides the AGB code used for billing. If null, the organization\'s default AGB code is used. 
     */
    'locationId'?: string | null;
    /**
     * Name of the linked location (denormalized for display)
     */
    'locationName'?: string | null;
    /**
     * Reference to the unified settings table (billing domain). This is the new source of truth for treatment setting. 
     */
    'settingId'?: string | null;
    'setting'?: Setting;
    /**
     * Human-readable setting description
     */
    'settingDescription'?: string;
    /**
     * Start date of the zorgtraject
     */
    'startdatum': string;
    /**
     * End date of the zorgtraject (null if still active)
     */
    'einddatum'?: string | null;
    /**
     * User ID of the regiebehandelaar — the practitioner who bears professional responsibility for the zorgtraject. Selected from the organization\'s designated regiebehandelaren.  Absent when the zorgtraject names none. The regiebehandelaar is a Zvw role, so only a regime that does not require one allows that — a `JEUGDWET` zorgtraject may have none.  A Scrivio user id. Outside the app it can only be matched against the `userId` that getClientBehandelaars returns. 
     */
    'regiebehandelaarId'?: string;
    /**
     * Full name of the regiebehandelaar. Absent when `regiebehandelaarId` is.
     */
    'regiebehandelaarName'?: string;
    /**
     * User ID of the primair behandelaar (dossierverantwoordelijke) — the practitioner who carries out the treatment and maintains the dossier. Selected from the client\'s behandelteam. Null when not yet assigned.  Read-only projection of the currently active period; assign it at creation time or change it via `POST /zorgtrajecten/{id}/primair-behandelaar/wijzigen`.  A Scrivio user id. Outside the app it can only be matched against the `userId` that getClientBehandelaars returns. 
     */
    'primairBehandelaarId'?: string | null;
    /**
     * Full name of the primair behandelaar, or null when unassigned
     */
    'primairBehandelaarName'?: string | null;
    /**
     * Link to current active zorgvraagtypering
     */
    'zorgvraagtyperingId'?: string | null;
    /**
     * Current zorgvraagtypering code
     */
    'zorgvraagtyperingCode'?: ZorgvraagtyperingCode | null;
    /**
     * Current zorgvraagtypering description
     */
    'zorgvraagtyperingDescription'?: string | null;
    /**
     * Start date of current zorgvraagtypering
     */
    'zorgvraagtyperingStartdatum'?: string | null;
    /**
     * Number of herijkingen performed on this zorgtraject. Computed for display in the Scrivio app.
     */
    'herijkingCount'?: number | null;
    /**
     * The DSM hoofdgroep currently recorded on the zorgtraject — the `Diagnosecode` a gespecialiseerde-ggz declaration states under Diagnosecodelijst `029`.  Read-only projection of the GGZ-profiel period with the latest startdatum; record it at creation time or append a period via `POST /zorgtrajecten/{id}/ggz-profiel/wijzigen`. 
     */
    'dsmHoofdgroepCode'?: string | null;
    /**
     * Betekenis of the current DSM hoofdgroep, exactly as CL0005-NZA states it
     */
    'dsmHoofdgroepBetekenis'?: string | null;
    /**
     * Start date of the current GGZ-profiel period
     */
    'dsmHoofdgroepStartdatum'?: string | null;
    'voorgesteldeDsmHoofdgroep'?: VoorgesteldeDsmHoofdgroep;
    /**
     * Structured DSM-5 diagnoses from the official NZa codelist
     */
    'dsm5Diagnoses'?: Array<Dsm5DiagnosisSummary>;
    /**
     * Diagnose wording no DSM-5 code expresses — what the retired free-text diagnose periods carried, one line per period, and what a ZPM import whose code resolves to nothing records. Read-only: there is no write path, and nothing declares from it. The DSM-5 diagnosis is `dsm5Diagnoses`. 
     */
    'diagnoseVrijeTekst'?: string | null;
    /**
     * Which kind of ggz this zorgtraject delivers, and so which diagnosecodelijst its declaration carries. Null when the type was never recorded.  Read-only projection of the GGZ-profiel period with the latest startdatum — the type is versioned together with the diagnosis it selects, so it is changed by appending a period via `POST /zorgtrajecten/{id}/ggz-profiel/wijzigen`. 
     */
    'ggzType'?: GgzType | null;
    /**
     * GB-ggz behandelprofiel, the diagnosis a basis-ggz declaration carries (codelijst `033`). Only belongs on a `BASIS_GGZ` trajectory — the write paths refuse to record one on a `GESPECIALISEERDE_GGZ` trajectory.  Read-only projection of the current GGZ-profiel period, like `ggzType`. 
     */
    'basisGgzProfiel'?: BasisGgzProfiel | null;
    /**
     * Which financing regime (wettelijk kader) funds this zorgtraject.  Null means the regime was never recorded; such a zorgtraject is validated exactly as a `ZVW` one. Stated on create (`CreateZorgtrajectRequest.financingRegime`) or by an import, and never changed afterwards. 
     */
    'financingRegime'?: FinancingRegime | null;
    /**
     * Care intensity level for residential billing (verblijfsdagen). Required when the client has VERBLIJF appointment types for proper VD code selection. Values A-E correspond to different care levels. 
     */
    'careIntensity'?: CareIntensity | null;
    /**
     * Whether a referral (verwijzing) is present.  When `true`, `verwijzerType`, `verwijzerNaam` and `verwijsdatum` are populated, and `agbVerwijzer` too unless `verwijstype` is `VERWIJZING_AANWEZIG_ZONDER_AGB` — the write paths enforce it, so the ZPM invoice can render the verwijzer block in full. 
     */
    'verwijzingAanwezig': boolean;
    /**
     * Type of referrer. Populated when `verwijzingAanwezig` is true.
     */
    'verwijzerType'?: VerwijzerType | null;
    /**
     * Type verwijzing per Vektis CL0004-NZA, shown on de restitutienota
     */
    'verwijstype'?: VerwijstypeCode | null;
    /**
     * AGB code of the verwijzer. Populated when `verwijzingAanwezig` is true, unless `verwijstype` is `VERWIJZING_AANWEZIG_ZONDER_AGB` (Vektis CL0004-NZA 07) — the code that declares this verwijzer has no AGB-code. 
     */
    'agbVerwijzer'?: string | null;
    /**
     * Date of the referral (verwijsdatum) for GDS801 XML generation. Populated when `verwijzingAanwezig` is true. 
     */
    'verwijsdatum'?: string | null;
    /**
     * Link to verwijsbrief document
     */
    'verwijsbriefDocumentId'?: string | null;
    /**
     * Name of the referrer (e.g., initials + surname). Populated when `verwijzingAanwezig` is true. 
     */
    'verwijzerNaam'?: string | null;
    /**
     * Email address of the referrer, used for sending letters via Zivver
     */
    'verwijzerEmail'?: string | null;
    'status': ZorgtrajectStatus;
    /**
     * Timestamp when zorgtraject was created (milliseconds since epoch)
     */
    'createdAt': number;
    /**
     * Timestamp when zorgtraject was last updated (milliseconds since epoch)
     */
    'updatedAt': number;
    /**
     * Whether this zorgtraject has any appointments, cancelled ones included. Kept for clients that predate `earliestAppointmentDate`; new callers should read that field, which answers the same question and also says how far the startdatum may move.
     */
    'hasAppointments': boolean;
    /**
     * The date of the zorgtraject\'s earliest afspraak — its Amsterdam calendar date, cancelled afspraken included — or null when it has none. The startdatum may be corrected to any date on or before it, and is refused (409) beyond it.
     */
    'earliestAppointmentDate'?: string | null;
    /**
     * Whether the startdatum is fixed because the insurer already holds a declaration carrying it: a regel in an exported batch that is in it, was sent or was accepted. The insurer pins the startdatum per zorgtrajectnummer, so a change would make every later declaration be rejected, and an update that changes it is refused (409). A declaration that was rejected or credited does not count.
     */
    'startdatumVergrendeld': boolean;
}


/**
 * Zorgtraject status: - ACTIEF: Active zorgtraject - AFGESLOTEN: Closed zorgtraject 
 */

export const ZorgtrajectStatus = {
    Actief: 'ACTIEF',
    Afgesloten: 'AFGESLOTEN'
} as const;

export type ZorgtrajectStatus = typeof ZorgtrajectStatus[keyof typeof ZorgtrajectStatus];


/**
 * Lightweight zorgtraject info for display in appointment context
 */
export interface ZorgtrajectSummary {
    /**
     * Zorgtraject unique identifier
     */
    'id': string;
    /**
     * Optional descriptive name/title
     */
    'name'?: string | null;
    /**
     * Location ID for matching by appointment location
     */
    'locationId'?: string | null;
    /**
     * Location name for display
     */
    'locationName'?: string | null;
    /**
     * Human-readable setting description
     */
    'settingDescription'?: string | null;
    'status': ZorgtrajectStatus;
    /**
     * Start date
     */
    'startdatum': string;
    /**
     * End date (null if open-ended)
     */
    'einddatum'?: string | null;
    /**
     * Name of the regiebehandelaar
     */
    'regiebehandelaarName'?: string | null;
}


/**
 * Zorgvraagtypering codes as defined by NZa, published as Vektis codelijst CL0007-NZA (Diagnosecodelijst 031). 20 codes: the numbering jumps ZT08 -> ZT10. 
 */

export const ZorgvraagtyperingCode = {
    Zt01: 'ZT01',
    Zt02: 'ZT02',
    Zt03: 'ZT03',
    Zt04: 'ZT04',
    Zt05: 'ZT05',
    Zt06: 'ZT06',
    Zt07: 'ZT07',
    Zt08: 'ZT08',
    Zt10: 'ZT10',
    Zt11: 'ZT11',
    Zt12: 'ZT12',
    Zt13: 'ZT13',
    Zt14: 'ZT14',
    Zt15: 'ZT15',
    Zt16: 'ZT16',
    Zt17: 'ZT17',
    Zt18: 'ZT18',
    Zt19: 'ZT19',
    Zt20: 'ZT20',
    Zt21: 'ZT21'
} as const;

export type ZorgvraagtyperingCode = typeof ZorgvraagtyperingCode[keyof typeof ZorgvraagtyperingCode];



/**
 * ZorgtrajectenApi - axios parameter creator
 */
export const ZorgtrajectenApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Records an adjustment on top of a toewijzing — a new volume, a new einddatum, or both, from an `ingangsdatum`, with the practice\'s reason (ADR-030 V1). For a gemeente that approves an increase or an extension by email, or lets the practice push the einddatum out, and sends no JW301. Allowed on a `MANUAL` and a `JW301` toewijzing alike, to whoever may update the zorgtraject.  What the toewijzing stores stays as received; the response\'s `geldendeOmvang` and `geldendeEinddatum` are what applies now. A later JW301 for the same `toewijzingNummer` supersedes every adjustment recorded before it, as does an `updateToewijzing` that changes what a `MANUAL` toewijzing allocated. 
         * @summary Record a change the gemeente approved without a message
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {string} toewijzingId Toewijzing UUID
         * @param {ToewijzingAanpassingRequest} toewijzingAanpassingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        adjustToewijzing: async (zorgtrajectId: string, toewijzingId: string, toewijzingAanpassingRequest: ToewijzingAanpassingRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('adjustToewijzing', 'zorgtrajectId', zorgtrajectId)
            // verify required parameter 'toewijzingId' is not null or undefined
            assertParamExists('adjustToewijzing', 'toewijzingId', toewijzingId)
            // verify required parameter 'toewijzingAanpassingRequest' is not null or undefined
            assertParamExists('adjustToewijzing', 'toewijzingAanpassingRequest', toewijzingAanpassingRequest)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/toewijzingen/{toewijzingId}/aanpassingen`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)))
                .replace(`{${"toewijzingId"}}`, encodeURIComponent(String(toewijzingId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(toewijzingAanpassingRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Checks whether the prerequisites for creating a behandelconsult (treatment appointment) are met for this zorgtraject.  A behandelconsult requires: 1. A DSM-5 diagnosis is set on the zorgtraject 2. At least one completed diagnostic appointment with the regiebehandelaar  This endpoint allows the frontend to proactively warn the user when prerequisites are not met. The prerequisites are advisory only — neither this endpoint nor appointment creation blocks on them. 
         * @summary Check behandelconsult prerequisites
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        checkBehandelconsultPrerequisites: async (zorgtrajectId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('checkBehandelconsultPrerequisites', 'zorgtrajectId', zorgtrajectId)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/behandelconsult-prerequisites`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * An aspecifieke toewijzing names only a productcategorie, and a gemeente declaration names one productcode. Where the contracts hold several products in the categorie, the practice chooses one here, once for the whole toewijzing; a null `productCode` clears the choice. Allowed on a `MANUAL`, `JW301` and `IMPORT` toewijzing alike, to whoever may update the zorgtraject (the `update` capability).  The choice is stored as `gekozenProductCode`, beside the gemeente\'s `productCode`, and is never sent to the gemeente as its own. A later JW301 keeps it, unless that JW301 names a productcode or another categorie. Months held for want of a product are declared on the next line generation; a month already declared is not declared again. 
         * @summary Choose the product an aspecifieke toewijzing is declared as
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {string} toewijzingId Toewijzing UUID
         * @param {ToewijzingProductKeuzeRequest} toewijzingProductKeuzeRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        chooseToewijzingProduct: async (zorgtrajectId: string, toewijzingId: string, toewijzingProductKeuzeRequest: ToewijzingProductKeuzeRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('chooseToewijzingProduct', 'zorgtrajectId', zorgtrajectId)
            // verify required parameter 'toewijzingId' is not null or undefined
            assertParamExists('chooseToewijzingProduct', 'toewijzingId', toewijzingId)
            // verify required parameter 'toewijzingProductKeuzeRequest' is not null or undefined
            assertParamExists('chooseToewijzingProduct', 'toewijzingProductKeuzeRequest', toewijzingProductKeuzeRequest)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/toewijzingen/{toewijzingId}/product`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)))
                .replace(`{${"toewijzingId"}}`, encodeURIComponent(String(toewijzingId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(toewijzingProductKeuzeRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Closes a zorgtraject by setting einddatum and status to AFGESLOTEN. Once closed, the zorgtraject cannot be modified except via reopen.  On a Jeugdwet zorgtraject, every toewijzing whose `stopbericht` is still owed — not `BEEINDIGD`, and no JW307 `KLAARGEZET` — is answered in the same call (#4816): the ones named in `stopberichten` get a JW307 queued in the outbox, and every other one is `UITGESTELD`, which raises a signal on the zorgtraject until its stop is queued from the toewijzing. A stop message is never sent that the request did not name. 
         * @summary Close zorgtraject
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {CloseZorgtrajectRequest} closeZorgtrajectRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        closeZorgtraject: async (zorgtrajectId: string, closeZorgtrajectRequest: CloseZorgtrajectRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('closeZorgtraject', 'zorgtrajectId', zorgtrajectId)
            // verify required parameter 'closeZorgtrajectRequest' is not null or undefined
            assertParamExists('closeZorgtraject', 'closeZorgtrajectRequest', closeZorgtrajectRequest)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/close`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(closeZorgtrajectRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Registers direct, indirect and travel minutes a medewerker delivered on the zorgtraject, to whoever may update the zorgtraject (the `update` capability). Nothing reads a registration to generate a billing line yet. 
         * @summary Register delivered care
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {RegistratieRequest} registratieRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createRegistratie: async (zorgtrajectId: string, registratieRequest: RegistratieRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('createRegistratie', 'zorgtrajectId', zorgtrajectId)
            // verify required parameter 'registratieRequest' is not null or undefined
            assertParamExists('createRegistratie', 'registratieRequest', registratieRequest)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/registraties`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(registratieRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Records a toewijzing entered by hand (`source: MANUAL`), until JW301 messages are live. Allowed only on a `JEUGDWET` or `WMO` zorgtraject, to whoever may update the zorgtraject (the `update` capability). Nothing reads a toewijzing to generate a billing line yet. 
         * @summary Record a toewijzing by hand
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {ToewijzingRequest} toewijzingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createToewijzing: async (zorgtrajectId: string, toewijzingRequest: ToewijzingRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('createToewijzing', 'zorgtrajectId', zorgtrajectId)
            // verify required parameter 'toewijzingRequest' is not null or undefined
            assertParamExists('createToewijzing', 'toewijzingRequest', toewijzingRequest)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/toewijzingen`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(toewijzingRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Creates a new zorgtraject for a client in the authenticated user\'s organization
         * @summary Create new zorgtraject for client
         * @param {string} clientId Client UUID
         * @param {CreateZorgtrajectRequest} createZorgtrajectRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createZorgtraject: async (clientId: string, createZorgtrajectRequest: CreateZorgtrajectRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('createZorgtraject', 'clientId', clientId)
            // verify required parameter 'createZorgtrajectRequest' is not null or undefined
            assertParamExists('createZorgtraject', 'createZorgtrajectRequest', createZorgtrajectRequest)
            const localVarPath = `/api/v1/clients/{clientId}/zorgtrajecten`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(createZorgtrajectRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Removes a registration that should never have been made; the audit log keeps the removal.
         * @summary Remove a registration entered in error
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {string} registratieId Registratie UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteRegistratie: async (zorgtrajectId: string, registratieId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('deleteRegistratie', 'zorgtrajectId', zorgtrajectId)
            // verify required parameter 'registratieId' is not null or undefined
            assertParamExists('deleteRegistratie', 'registratieId', registratieId)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/registraties/{registratieId}`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)))
                .replace(`{${"registratieId"}}`, encodeURIComponent(String(registratieId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Removes a `MANUAL` toewijzing that should never have been recorded; the audit log keeps the removal. A `JW301` toewijzing is refused with `409`. 
         * @summary Remove a toewijzing entered in error
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {string} toewijzingId Toewijzing UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteToewijzing: async (zorgtrajectId: string, toewijzingId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('deleteToewijzing', 'zorgtrajectId', zorgtrajectId)
            // verify required parameter 'toewijzingId' is not null or undefined
            assertParamExists('deleteToewijzing', 'toewijzingId', toewijzingId)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/toewijzingen/{toewijzingId}`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)))
                .replace(`{${"toewijzingId"}}`, encodeURIComponent(String(toewijzingId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Find the active zorgtraject that should be used for an appointment on a specific date.  Selection logic: 1. Client has 1 active zorgtraject → return it 2. Client has multiple active zorgtrajecten → return them all (future: match by location) 3. Client has no active zorgtraject → return empty with warning  A zorgtraject is considered active for a date if: - Status = ACTIEF - startdatum <= date - einddatum is null OR einddatum >= date 
         * @summary Find matching zorgtraject for appointment
         * @param {string} clientId Client UUID
         * @param {string} date Date for which to find matching zorgtraject (typically appointment date)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        findMatchingZorgtraject: async (clientId: string, date: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('findMatchingZorgtraject', 'clientId', clientId)
            // verify required parameter 'date' is not null or undefined
            assertParamExists('findMatchingZorgtraject', 'date', date)
            const localVarPath = `/api/v1/clients/{clientId}/zorgtrajecten/match`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (date !== undefined) {
                localVarQueryParameter['date'] = (date as any instanceof Date) ?
                    (date as any).toISOString().substring(0,10) :
                    date;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieves a single zorgtraject by ID. User must be in same organization.
         * @summary Get zorgtraject by ID
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getZorgtrajectById: async (zorgtrajectId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('getZorgtrajectById', 'zorgtrajectId', zorgtrajectId)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns authorization capabilities for a specific zorgtraject. This endpoint is used by the frontend to determine which actions the current user can perform on the zorgtraject. 
         * @summary Get zorgtraject capabilities
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getZorgtrajectCapabilities: async (zorgtrajectId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('getZorgtrajectCapabilities', 'zorgtrajectId', zorgtrajectId)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/capabilities`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Puts one practitioner\'s session with one cliënt on a toewijzing that covers it: registers it there when the afspraak was waiting on the choice, moves its registration there when it was on another. The rules of `updateRegistratie` apply to a move, so a session in a month already declared to the gemeente cannot move until that month is credited. 
         * @summary Choose the toewijzing a session went to
         * @param {string} appointmentId Appointment UUID
         * @param {KiesToewijzingRequest} kiesToewijzingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        kiesAppointmentToewijzing: async (appointmentId: string, kiesToewijzingRequest: KiesToewijzingRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'appointmentId' is not null or undefined
            assertParamExists('kiesAppointmentToewijzing', 'appointmentId', appointmentId)
            // verify required parameter 'kiesToewijzingRequest' is not null or undefined
            assertParamExists('kiesAppointmentToewijzing', 'kiesToewijzingRequest', kiesToewijzingRequest)
            const localVarPath = `/api/v1/appointments/{appointmentId}/toewijzing-keuzes`
                .replace(`{${"appointmentId"}}`, encodeURIComponent(String(appointmentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(kiesToewijzingRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * One entry per present cliënt on a Jeugdwet zorgtraject and attending practitioner of an afgeronde afspraak (#4819): the toewijzingen that name the practitioner and cover the session, and the registration it became. `open` is true when more than one covers it and nobody has chosen yet — nothing is registered until someone does. Empty for an afspraak that is not `COMPLETED` or has no Jeugdwet cliënt. 
         * @summary Which toewijzing each Jeugdwet session of an afspraak went to
         * @param {string} appointmentId Appointment UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAppointmentToewijzingKeuzes: async (appointmentId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'appointmentId' is not null or undefined
            assertParamExists('listAppointmentToewijzingKeuzes', 'appointmentId', appointmentId)
            const localVarPath = `/api/v1/appointments/{appointmentId}/toewijzing-keuzes`
                .replace(`{${"appointmentId"}}`, encodeURIComponent(String(appointmentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List zorgtrajecten for a specific client with pagination and filtering. Results are ordered by startdatum descending (most recent first). 
         * @summary List zorgtrajecten for client
         * @param {string} clientId Client UUID
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page
         * @param {ZorgtrajectStatus} [status] Filter by status
         * @param {boolean} [includeAfgesloten] Include afgesloten (closed) zorgtrajecten
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listClientZorgtrajecten: async (clientId: string, page?: number, size?: number, status?: ZorgtrajectStatus, includeAfgesloten?: boolean, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('listClientZorgtrajecten', 'clientId', clientId)
            const localVarPath = `/api/v1/clients/{clientId}/zorgtrajecten`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            if (status !== undefined) {
                localVarQueryParameter['status'] = status;
            }

            if (includeAfgesloten !== undefined) {
                localVarQueryParameter['includeAfgesloten'] = includeAfgesloten;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List all zorgtrajecten in the authenticated user\'s organization with pagination, search, and filtering capabilities. Useful for admin/overview purposes. 
         * @summary List all zorgtrajecten in organization
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page
         * @param {string} [search] Search query (searches client name)
         * @param {ZorgtrajectStatus} [status] Filter by status
         * @param {string} [regiebehandelaarId] Filter by regiebehandelaar user ID
         * @param {string} [startdatumFrom] Filter by minimum startdatum
         * @param {string} [startdatumTo] Filter by maximum startdatum
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listOrganizationZorgtrajecten: async (page?: number, size?: number, search?: string, status?: ZorgtrajectStatus, regiebehandelaarId?: string, startdatumFrom?: string, startdatumTo?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/zorgtrajecten`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            if (search !== undefined) {
                localVarQueryParameter['search'] = search;
            }

            if (status !== undefined) {
                localVarQueryParameter['status'] = status;
            }

            if (regiebehandelaarId !== undefined) {
                localVarQueryParameter['regiebehandelaarId'] = regiebehandelaarId;
            }

            if (startdatumFrom !== undefined) {
                localVarQueryParameter['startdatumFrom'] = (startdatumFrom as any instanceof Date) ?
                    (startdatumFrom as any).toISOString().substring(0,10) :
                    startdatumFrom;
            }

            if (startdatumTo !== undefined) {
                localVarQueryParameter['startdatumTo'] = (startdatumTo as any instanceof Date) ?
                    (startdatumTo as any).toISOString().substring(0,10) :
                    startdatumTo;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Every registration of delivered care on the zorgtraject — with or without an appointment behind it (ADR-030 addendum A4) — most recent `datum` first. `directMinutes` is resolved: for a registration that names an appointment it is that appointment\'s duration now. 
         * @summary List the care registered on a zorgtraject
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listRegistraties: async (zorgtrajectId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('listRegistraties', 'zorgtrajectId', zorgtrajectId)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/registraties`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Every toewijzing the zorgtraject holds — a gemeente\'s allocation of one product each (ADR-030 addendum A1) — most recent ingangsdatum first. Empty for a zorgtraject that holds none, which includes every zorgtraject outside Jeugdwet and Wmo. 
         * @summary List a zorgtraject\'s toewijzingen
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listToewijzingen: async (zorgtrajectId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('listToewijzingen', 'zorgtrajectId', zorgtrajectId)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/toewijzingen`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Builds a new JW305 for a toewijzing whose latest start message the gemeente\'s JW306 rejected (`startberichtAfkeuring`), from the dossier as it is now, and queues it in the outbox (#4866). The practice corrects what the retourcodes name first. The start is dated as the first was: the earliest afgeronde afspraak registered against the toewijzing, never before its `ingangsdatum`. Declaring against the toewijzing stays held until a JW306 accepts this one.  Held to the zorgtraject\'s `close` capability, like the stop message: a closed zorgtraject\'s lines are held by a rejected start as much as a running one\'s. 
         * @summary Send a rejected start message again
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {string} toewijzingId Toewijzing UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        queueToewijzingStartbericht: async (zorgtrajectId: string, toewijzingId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('queueToewijzingStartbericht', 'zorgtrajectId', zorgtrajectId)
            // verify required parameter 'toewijzingId' is not null or undefined
            assertParamExists('queueToewijzingStartbericht', 'toewijzingId', toewijzingId)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/toewijzingen/{toewijzingId}/startbericht`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)))
                .replace(`{${"toewijzingId"}}`, encodeURIComponent(String(toewijzingId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Builds a JW307 saying delivery against the toewijzing stopped on `einddatum`, for `redenBeeindiging`, and queues it in the outbox (#4816) — the stop a closed zorgtraject deferred, or one for a toewijzing whose delivery ended while the zorgtraject runs on. Clears the deferral and its signal. Only on a Jeugdwet zorgtraject, and only once per toewijzing.  Held to the zorgtraject\'s `close` capability rather than `update`: a closed zorgtraject refuses every update, and sending the stop its closing deferred is part of closing it. 
         * @summary Queue a toewijzing\'s stop message
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {string} toewijzingId Toewijzing UUID
         * @param {ToewijzingStopberichtRequest} toewijzingStopberichtRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        queueToewijzingStopbericht: async (zorgtrajectId: string, toewijzingId: string, toewijzingStopberichtRequest: ToewijzingStopberichtRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('queueToewijzingStopbericht', 'zorgtrajectId', zorgtrajectId)
            // verify required parameter 'toewijzingId' is not null or undefined
            assertParamExists('queueToewijzingStopbericht', 'toewijzingId', toewijzingId)
            // verify required parameter 'toewijzingStopberichtRequest' is not null or undefined
            assertParamExists('queueToewijzingStopbericht', 'toewijzingStopberichtRequest', toewijzingStopberichtRequest)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/toewijzingen/{toewijzingId}/stopbericht`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)))
                .replace(`{${"toewijzingId"}}`, encodeURIComponent(String(toewijzingId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(toewijzingStopberichtRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Reopens a closed zorgtraject by clearing einddatum and setting status to ACTIEF. 
         * @summary Reopen zorgtraject
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        reopenZorgtraject: async (zorgtrajectId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('reopenZorgtraject', 'zorgtrajectId', zorgtrajectId)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/reopen`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Replaces everything a registration says. The rules are those of `createRegistratie`.
         * @summary Change a registration
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {string} registratieId Registratie UUID
         * @param {RegistratieRequest} registratieRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateRegistratie: async (zorgtrajectId: string, registratieId: string, registratieRequest: RegistratieRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('updateRegistratie', 'zorgtrajectId', zorgtrajectId)
            // verify required parameter 'registratieId' is not null or undefined
            assertParamExists('updateRegistratie', 'registratieId', registratieId)
            // verify required parameter 'registratieRequest' is not null or undefined
            assertParamExists('updateRegistratie', 'registratieRequest', registratieRequest)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/registraties/{registratieId}`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)))
                .replace(`{${"registratieId"}}`, encodeURIComponent(String(registratieId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(registratieRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Replaces everything a `MANUAL` toewijzing says, including who delivers against it. A `JW301` toewijzing is the gemeente\'s message and is refused with `409`: a changed allocation arrives as a new message, and one the gemeente approved without a message is recorded with `adjustToewijzing`. 
         * @summary Change a toewijzing entered by hand
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {string} toewijzingId Toewijzing UUID
         * @param {ToewijzingRequest} toewijzingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateToewijzing: async (zorgtrajectId: string, toewijzingId: string, toewijzingRequest: ToewijzingRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('updateToewijzing', 'zorgtrajectId', zorgtrajectId)
            // verify required parameter 'toewijzingId' is not null or undefined
            assertParamExists('updateToewijzing', 'toewijzingId', toewijzingId)
            // verify required parameter 'toewijzingRequest' is not null or undefined
            assertParamExists('updateToewijzing', 'toewijzingRequest', toewijzingRequest)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/toewijzingen/{toewijzingId}`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)))
                .replace(`{${"toewijzingId"}}`, encodeURIComponent(String(toewijzingId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(toewijzingRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Updates an existing zorgtraject. User must be in same organization. Note: Setting cannot be changed after creation - must close and create new zorgtraject.  **Startdatum editing rules:** - Startdatum can only be changed if no appointments exist for this zorgtraject - When startdatum changes, it cascades to the first period of all versioned fields - Returns 409 Conflict if appointments exist and startdatum change is attempted 
         * @summary Update zorgtraject
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {UpdateZorgtrajectRequest} updateZorgtrajectRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateZorgtraject: async (zorgtrajectId: string, updateZorgtrajectRequest: UpdateZorgtrajectRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('updateZorgtraject', 'zorgtrajectId', zorgtrajectId)
            // verify required parameter 'updateZorgtrajectRequest' is not null or undefined
            assertParamExists('updateZorgtraject', 'updateZorgtrajectRequest', updateZorgtrajectRequest)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateZorgtrajectRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ZorgtrajectenApi - functional programming interface
 */
export const ZorgtrajectenApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ZorgtrajectenApiAxiosParamCreator(configuration)
    return {
        /**
         * Records an adjustment on top of a toewijzing — a new volume, a new einddatum, or both, from an `ingangsdatum`, with the practice\'s reason (ADR-030 V1). For a gemeente that approves an increase or an extension by email, or lets the practice push the einddatum out, and sends no JW301. Allowed on a `MANUAL` and a `JW301` toewijzing alike, to whoever may update the zorgtraject.  What the toewijzing stores stays as received; the response\'s `geldendeOmvang` and `geldendeEinddatum` are what applies now. A later JW301 for the same `toewijzingNummer` supersedes every adjustment recorded before it, as does an `updateToewijzing` that changes what a `MANUAL` toewijzing allocated. 
         * @summary Record a change the gemeente approved without a message
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {string} toewijzingId Toewijzing UUID
         * @param {ToewijzingAanpassingRequest} toewijzingAanpassingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async adjustToewijzing(zorgtrajectId: string, toewijzingId: string, toewijzingAanpassingRequest: ToewijzingAanpassingRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ToewijzingResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.adjustToewijzing(zorgtrajectId, toewijzingId, toewijzingAanpassingRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ZorgtrajectenApi.adjustToewijzing']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Checks whether the prerequisites for creating a behandelconsult (treatment appointment) are met for this zorgtraject.  A behandelconsult requires: 1. A DSM-5 diagnosis is set on the zorgtraject 2. At least one completed diagnostic appointment with the regiebehandelaar  This endpoint allows the frontend to proactively warn the user when prerequisites are not met. The prerequisites are advisory only — neither this endpoint nor appointment creation blocks on them. 
         * @summary Check behandelconsult prerequisites
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async checkBehandelconsultPrerequisites(zorgtrajectId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BehandelconsultPrerequisiteStatus>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.checkBehandelconsultPrerequisites(zorgtrajectId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ZorgtrajectenApi.checkBehandelconsultPrerequisites']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * An aspecifieke toewijzing names only a productcategorie, and a gemeente declaration names one productcode. Where the contracts hold several products in the categorie, the practice chooses one here, once for the whole toewijzing; a null `productCode` clears the choice. Allowed on a `MANUAL`, `JW301` and `IMPORT` toewijzing alike, to whoever may update the zorgtraject (the `update` capability).  The choice is stored as `gekozenProductCode`, beside the gemeente\'s `productCode`, and is never sent to the gemeente as its own. A later JW301 keeps it, unless that JW301 names a productcode or another categorie. Months held for want of a product are declared on the next line generation; a month already declared is not declared again. 
         * @summary Choose the product an aspecifieke toewijzing is declared as
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {string} toewijzingId Toewijzing UUID
         * @param {ToewijzingProductKeuzeRequest} toewijzingProductKeuzeRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async chooseToewijzingProduct(zorgtrajectId: string, toewijzingId: string, toewijzingProductKeuzeRequest: ToewijzingProductKeuzeRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ToewijzingResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.chooseToewijzingProduct(zorgtrajectId, toewijzingId, toewijzingProductKeuzeRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ZorgtrajectenApi.chooseToewijzingProduct']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Closes a zorgtraject by setting einddatum and status to AFGESLOTEN. Once closed, the zorgtraject cannot be modified except via reopen.  On a Jeugdwet zorgtraject, every toewijzing whose `stopbericht` is still owed — not `BEEINDIGD`, and no JW307 `KLAARGEZET` — is answered in the same call (#4816): the ones named in `stopberichten` get a JW307 queued in the outbox, and every other one is `UITGESTELD`, which raises a signal on the zorgtraject until its stop is queued from the toewijzing. A stop message is never sent that the request did not name. 
         * @summary Close zorgtraject
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {CloseZorgtrajectRequest} closeZorgtrajectRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async closeZorgtraject(zorgtrajectId: string, closeZorgtrajectRequest: CloseZorgtrajectRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ZorgtrajectResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.closeZorgtraject(zorgtrajectId, closeZorgtrajectRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ZorgtrajectenApi.closeZorgtraject']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Registers direct, indirect and travel minutes a medewerker delivered on the zorgtraject, to whoever may update the zorgtraject (the `update` capability). Nothing reads a registration to generate a billing line yet. 
         * @summary Register delivered care
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {RegistratieRequest} registratieRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createRegistratie(zorgtrajectId: string, registratieRequest: RegistratieRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<RegistratieResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createRegistratie(zorgtrajectId, registratieRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ZorgtrajectenApi.createRegistratie']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Records a toewijzing entered by hand (`source: MANUAL`), until JW301 messages are live. Allowed only on a `JEUGDWET` or `WMO` zorgtraject, to whoever may update the zorgtraject (the `update` capability). Nothing reads a toewijzing to generate a billing line yet. 
         * @summary Record a toewijzing by hand
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {ToewijzingRequest} toewijzingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createToewijzing(zorgtrajectId: string, toewijzingRequest: ToewijzingRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ToewijzingResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createToewijzing(zorgtrajectId, toewijzingRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ZorgtrajectenApi.createToewijzing']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Creates a new zorgtraject for a client in the authenticated user\'s organization
         * @summary Create new zorgtraject for client
         * @param {string} clientId Client UUID
         * @param {CreateZorgtrajectRequest} createZorgtrajectRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createZorgtraject(clientId: string, createZorgtrajectRequest: CreateZorgtrajectRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ZorgtrajectResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createZorgtraject(clientId, createZorgtrajectRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ZorgtrajectenApi.createZorgtraject']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Removes a registration that should never have been made; the audit log keeps the removal.
         * @summary Remove a registration entered in error
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {string} registratieId Registratie UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteRegistratie(zorgtrajectId: string, registratieId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteRegistratie(zorgtrajectId, registratieId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ZorgtrajectenApi.deleteRegistratie']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Removes a `MANUAL` toewijzing that should never have been recorded; the audit log keeps the removal. A `JW301` toewijzing is refused with `409`. 
         * @summary Remove a toewijzing entered in error
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {string} toewijzingId Toewijzing UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteToewijzing(zorgtrajectId: string, toewijzingId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteToewijzing(zorgtrajectId, toewijzingId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ZorgtrajectenApi.deleteToewijzing']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Find the active zorgtraject that should be used for an appointment on a specific date.  Selection logic: 1. Client has 1 active zorgtraject → return it 2. Client has multiple active zorgtrajecten → return them all (future: match by location) 3. Client has no active zorgtraject → return empty with warning  A zorgtraject is considered active for a date if: - Status = ACTIEF - startdatum <= date - einddatum is null OR einddatum >= date 
         * @summary Find matching zorgtraject for appointment
         * @param {string} clientId Client UUID
         * @param {string} date Date for which to find matching zorgtraject (typically appointment date)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async findMatchingZorgtraject(clientId: string, date: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ZorgtrajectMatchResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.findMatchingZorgtraject(clientId, date, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ZorgtrajectenApi.findMatchingZorgtraject']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Retrieves a single zorgtraject by ID. User must be in same organization.
         * @summary Get zorgtraject by ID
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getZorgtrajectById(zorgtrajectId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ZorgtrajectResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getZorgtrajectById(zorgtrajectId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ZorgtrajectenApi.getZorgtrajectById']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns authorization capabilities for a specific zorgtraject. This endpoint is used by the frontend to determine which actions the current user can perform on the zorgtraject. 
         * @summary Get zorgtraject capabilities
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getZorgtrajectCapabilities(zorgtrajectId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ZorgtrajectCapabilitiesResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getZorgtrajectCapabilities(zorgtrajectId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ZorgtrajectenApi.getZorgtrajectCapabilities']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Puts one practitioner\'s session with one cliënt on a toewijzing that covers it: registers it there when the afspraak was waiting on the choice, moves its registration there when it was on another. The rules of `updateRegistratie` apply to a move, so a session in a month already declared to the gemeente cannot move until that month is credited. 
         * @summary Choose the toewijzing a session went to
         * @param {string} appointmentId Appointment UUID
         * @param {KiesToewijzingRequest} kiesToewijzingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async kiesAppointmentToewijzing(appointmentId: string, kiesToewijzingRequest: KiesToewijzingRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ToewijzingKeuzeResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.kiesAppointmentToewijzing(appointmentId, kiesToewijzingRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ZorgtrajectenApi.kiesAppointmentToewijzing']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * One entry per present cliënt on a Jeugdwet zorgtraject and attending practitioner of an afgeronde afspraak (#4819): the toewijzingen that name the practitioner and cover the session, and the registration it became. `open` is true when more than one covers it and nobody has chosen yet — nothing is registered until someone does. Empty for an afspraak that is not `COMPLETED` or has no Jeugdwet cliënt. 
         * @summary Which toewijzing each Jeugdwet session of an afspraak went to
         * @param {string} appointmentId Appointment UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listAppointmentToewijzingKeuzes(appointmentId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<ToewijzingKeuzeResponse>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listAppointmentToewijzingKeuzes(appointmentId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ZorgtrajectenApi.listAppointmentToewijzingKeuzes']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * List zorgtrajecten for a specific client with pagination and filtering. Results are ordered by startdatum descending (most recent first). 
         * @summary List zorgtrajecten for client
         * @param {string} clientId Client UUID
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page
         * @param {ZorgtrajectStatus} [status] Filter by status
         * @param {boolean} [includeAfgesloten] Include afgesloten (closed) zorgtrajecten
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listClientZorgtrajecten(clientId: string, page?: number, size?: number, status?: ZorgtrajectStatus, includeAfgesloten?: boolean, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ZorgtrajectListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listClientZorgtrajecten(clientId, page, size, status, includeAfgesloten, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ZorgtrajectenApi.listClientZorgtrajecten']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * List all zorgtrajecten in the authenticated user\'s organization with pagination, search, and filtering capabilities. Useful for admin/overview purposes. 
         * @summary List all zorgtrajecten in organization
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page
         * @param {string} [search] Search query (searches client name)
         * @param {ZorgtrajectStatus} [status] Filter by status
         * @param {string} [regiebehandelaarId] Filter by regiebehandelaar user ID
         * @param {string} [startdatumFrom] Filter by minimum startdatum
         * @param {string} [startdatumTo] Filter by maximum startdatum
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listOrganizationZorgtrajecten(page?: number, size?: number, search?: string, status?: ZorgtrajectStatus, regiebehandelaarId?: string, startdatumFrom?: string, startdatumTo?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ZorgtrajectListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listOrganizationZorgtrajecten(page, size, search, status, regiebehandelaarId, startdatumFrom, startdatumTo, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ZorgtrajectenApi.listOrganizationZorgtrajecten']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Every registration of delivered care on the zorgtraject — with or without an appointment behind it (ADR-030 addendum A4) — most recent `datum` first. `directMinutes` is resolved: for a registration that names an appointment it is that appointment\'s duration now. 
         * @summary List the care registered on a zorgtraject
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listRegistraties(zorgtrajectId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<RegistratieResponse>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listRegistraties(zorgtrajectId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ZorgtrajectenApi.listRegistraties']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Every toewijzing the zorgtraject holds — a gemeente\'s allocation of one product each (ADR-030 addendum A1) — most recent ingangsdatum first. Empty for a zorgtraject that holds none, which includes every zorgtraject outside Jeugdwet and Wmo. 
         * @summary List a zorgtraject\'s toewijzingen
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listToewijzingen(zorgtrajectId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<ToewijzingResponse>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listToewijzingen(zorgtrajectId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ZorgtrajectenApi.listToewijzingen']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Builds a new JW305 for a toewijzing whose latest start message the gemeente\'s JW306 rejected (`startberichtAfkeuring`), from the dossier as it is now, and queues it in the outbox (#4866). The practice corrects what the retourcodes name first. The start is dated as the first was: the earliest afgeronde afspraak registered against the toewijzing, never before its `ingangsdatum`. Declaring against the toewijzing stays held until a JW306 accepts this one.  Held to the zorgtraject\'s `close` capability, like the stop message: a closed zorgtraject\'s lines are held by a rejected start as much as a running one\'s. 
         * @summary Send a rejected start message again
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {string} toewijzingId Toewijzing UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async queueToewijzingStartbericht(zorgtrajectId: string, toewijzingId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ToewijzingResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.queueToewijzingStartbericht(zorgtrajectId, toewijzingId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ZorgtrajectenApi.queueToewijzingStartbericht']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Builds a JW307 saying delivery against the toewijzing stopped on `einddatum`, for `redenBeeindiging`, and queues it in the outbox (#4816) — the stop a closed zorgtraject deferred, or one for a toewijzing whose delivery ended while the zorgtraject runs on. Clears the deferral and its signal. Only on a Jeugdwet zorgtraject, and only once per toewijzing.  Held to the zorgtraject\'s `close` capability rather than `update`: a closed zorgtraject refuses every update, and sending the stop its closing deferred is part of closing it. 
         * @summary Queue a toewijzing\'s stop message
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {string} toewijzingId Toewijzing UUID
         * @param {ToewijzingStopberichtRequest} toewijzingStopberichtRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async queueToewijzingStopbericht(zorgtrajectId: string, toewijzingId: string, toewijzingStopberichtRequest: ToewijzingStopberichtRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ToewijzingResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.queueToewijzingStopbericht(zorgtrajectId, toewijzingId, toewijzingStopberichtRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ZorgtrajectenApi.queueToewijzingStopbericht']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Reopens a closed zorgtraject by clearing einddatum and setting status to ACTIEF. 
         * @summary Reopen zorgtraject
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async reopenZorgtraject(zorgtrajectId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ZorgtrajectResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.reopenZorgtraject(zorgtrajectId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ZorgtrajectenApi.reopenZorgtraject']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Replaces everything a registration says. The rules are those of `createRegistratie`.
         * @summary Change a registration
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {string} registratieId Registratie UUID
         * @param {RegistratieRequest} registratieRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateRegistratie(zorgtrajectId: string, registratieId: string, registratieRequest: RegistratieRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<RegistratieResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateRegistratie(zorgtrajectId, registratieId, registratieRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ZorgtrajectenApi.updateRegistratie']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Replaces everything a `MANUAL` toewijzing says, including who delivers against it. A `JW301` toewijzing is the gemeente\'s message and is refused with `409`: a changed allocation arrives as a new message, and one the gemeente approved without a message is recorded with `adjustToewijzing`. 
         * @summary Change a toewijzing entered by hand
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {string} toewijzingId Toewijzing UUID
         * @param {ToewijzingRequest} toewijzingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateToewijzing(zorgtrajectId: string, toewijzingId: string, toewijzingRequest: ToewijzingRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ToewijzingResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateToewijzing(zorgtrajectId, toewijzingId, toewijzingRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ZorgtrajectenApi.updateToewijzing']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Updates an existing zorgtraject. User must be in same organization. Note: Setting cannot be changed after creation - must close and create new zorgtraject.  **Startdatum editing rules:** - Startdatum can only be changed if no appointments exist for this zorgtraject - When startdatum changes, it cascades to the first period of all versioned fields - Returns 409 Conflict if appointments exist and startdatum change is attempted 
         * @summary Update zorgtraject
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {UpdateZorgtrajectRequest} updateZorgtrajectRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateZorgtraject(zorgtrajectId: string, updateZorgtrajectRequest: UpdateZorgtrajectRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ZorgtrajectResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateZorgtraject(zorgtrajectId, updateZorgtrajectRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ZorgtrajectenApi.updateZorgtraject']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ZorgtrajectenApi - factory interface
 */
export const ZorgtrajectenApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ZorgtrajectenApiFp(configuration)
    return {
        /**
         * Records an adjustment on top of a toewijzing — a new volume, a new einddatum, or both, from an `ingangsdatum`, with the practice\'s reason (ADR-030 V1). For a gemeente that approves an increase or an extension by email, or lets the practice push the einddatum out, and sends no JW301. Allowed on a `MANUAL` and a `JW301` toewijzing alike, to whoever may update the zorgtraject.  What the toewijzing stores stays as received; the response\'s `geldendeOmvang` and `geldendeEinddatum` are what applies now. A later JW301 for the same `toewijzingNummer` supersedes every adjustment recorded before it, as does an `updateToewijzing` that changes what a `MANUAL` toewijzing allocated. 
         * @summary Record a change the gemeente approved without a message
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {string} toewijzingId Toewijzing UUID
         * @param {ToewijzingAanpassingRequest} toewijzingAanpassingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        adjustToewijzing(zorgtrajectId: string, toewijzingId: string, toewijzingAanpassingRequest: ToewijzingAanpassingRequest, options?: RawAxiosRequestConfig): AxiosPromise<ToewijzingResponse> {
            return localVarFp.adjustToewijzing(zorgtrajectId, toewijzingId, toewijzingAanpassingRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Checks whether the prerequisites for creating a behandelconsult (treatment appointment) are met for this zorgtraject.  A behandelconsult requires: 1. A DSM-5 diagnosis is set on the zorgtraject 2. At least one completed diagnostic appointment with the regiebehandelaar  This endpoint allows the frontend to proactively warn the user when prerequisites are not met. The prerequisites are advisory only — neither this endpoint nor appointment creation blocks on them. 
         * @summary Check behandelconsult prerequisites
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        checkBehandelconsultPrerequisites(zorgtrajectId: string, options?: RawAxiosRequestConfig): AxiosPromise<BehandelconsultPrerequisiteStatus> {
            return localVarFp.checkBehandelconsultPrerequisites(zorgtrajectId, options).then((request) => request(axios, basePath));
        },
        /**
         * An aspecifieke toewijzing names only a productcategorie, and a gemeente declaration names one productcode. Where the contracts hold several products in the categorie, the practice chooses one here, once for the whole toewijzing; a null `productCode` clears the choice. Allowed on a `MANUAL`, `JW301` and `IMPORT` toewijzing alike, to whoever may update the zorgtraject (the `update` capability).  The choice is stored as `gekozenProductCode`, beside the gemeente\'s `productCode`, and is never sent to the gemeente as its own. A later JW301 keeps it, unless that JW301 names a productcode or another categorie. Months held for want of a product are declared on the next line generation; a month already declared is not declared again. 
         * @summary Choose the product an aspecifieke toewijzing is declared as
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {string} toewijzingId Toewijzing UUID
         * @param {ToewijzingProductKeuzeRequest} toewijzingProductKeuzeRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        chooseToewijzingProduct(zorgtrajectId: string, toewijzingId: string, toewijzingProductKeuzeRequest: ToewijzingProductKeuzeRequest, options?: RawAxiosRequestConfig): AxiosPromise<ToewijzingResponse> {
            return localVarFp.chooseToewijzingProduct(zorgtrajectId, toewijzingId, toewijzingProductKeuzeRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Closes a zorgtraject by setting einddatum and status to AFGESLOTEN. Once closed, the zorgtraject cannot be modified except via reopen.  On a Jeugdwet zorgtraject, every toewijzing whose `stopbericht` is still owed — not `BEEINDIGD`, and no JW307 `KLAARGEZET` — is answered in the same call (#4816): the ones named in `stopberichten` get a JW307 queued in the outbox, and every other one is `UITGESTELD`, which raises a signal on the zorgtraject until its stop is queued from the toewijzing. A stop message is never sent that the request did not name. 
         * @summary Close zorgtraject
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {CloseZorgtrajectRequest} closeZorgtrajectRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        closeZorgtraject(zorgtrajectId: string, closeZorgtrajectRequest: CloseZorgtrajectRequest, options?: RawAxiosRequestConfig): AxiosPromise<ZorgtrajectResponse> {
            return localVarFp.closeZorgtraject(zorgtrajectId, closeZorgtrajectRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Registers direct, indirect and travel minutes a medewerker delivered on the zorgtraject, to whoever may update the zorgtraject (the `update` capability). Nothing reads a registration to generate a billing line yet. 
         * @summary Register delivered care
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {RegistratieRequest} registratieRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createRegistratie(zorgtrajectId: string, registratieRequest: RegistratieRequest, options?: RawAxiosRequestConfig): AxiosPromise<RegistratieResponse> {
            return localVarFp.createRegistratie(zorgtrajectId, registratieRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Records a toewijzing entered by hand (`source: MANUAL`), until JW301 messages are live. Allowed only on a `JEUGDWET` or `WMO` zorgtraject, to whoever may update the zorgtraject (the `update` capability). Nothing reads a toewijzing to generate a billing line yet. 
         * @summary Record a toewijzing by hand
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {ToewijzingRequest} toewijzingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createToewijzing(zorgtrajectId: string, toewijzingRequest: ToewijzingRequest, options?: RawAxiosRequestConfig): AxiosPromise<ToewijzingResponse> {
            return localVarFp.createToewijzing(zorgtrajectId, toewijzingRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Creates a new zorgtraject for a client in the authenticated user\'s organization
         * @summary Create new zorgtraject for client
         * @param {string} clientId Client UUID
         * @param {CreateZorgtrajectRequest} createZorgtrajectRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createZorgtraject(clientId: string, createZorgtrajectRequest: CreateZorgtrajectRequest, options?: RawAxiosRequestConfig): AxiosPromise<ZorgtrajectResponse> {
            return localVarFp.createZorgtraject(clientId, createZorgtrajectRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Removes a registration that should never have been made; the audit log keeps the removal.
         * @summary Remove a registration entered in error
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {string} registratieId Registratie UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteRegistratie(zorgtrajectId: string, registratieId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteRegistratie(zorgtrajectId, registratieId, options).then((request) => request(axios, basePath));
        },
        /**
         * Removes a `MANUAL` toewijzing that should never have been recorded; the audit log keeps the removal. A `JW301` toewijzing is refused with `409`. 
         * @summary Remove a toewijzing entered in error
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {string} toewijzingId Toewijzing UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteToewijzing(zorgtrajectId: string, toewijzingId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteToewijzing(zorgtrajectId, toewijzingId, options).then((request) => request(axios, basePath));
        },
        /**
         * Find the active zorgtraject that should be used for an appointment on a specific date.  Selection logic: 1. Client has 1 active zorgtraject → return it 2. Client has multiple active zorgtrajecten → return them all (future: match by location) 3. Client has no active zorgtraject → return empty with warning  A zorgtraject is considered active for a date if: - Status = ACTIEF - startdatum <= date - einddatum is null OR einddatum >= date 
         * @summary Find matching zorgtraject for appointment
         * @param {string} clientId Client UUID
         * @param {string} date Date for which to find matching zorgtraject (typically appointment date)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        findMatchingZorgtraject(clientId: string, date: string, options?: RawAxiosRequestConfig): AxiosPromise<ZorgtrajectMatchResponse> {
            return localVarFp.findMatchingZorgtraject(clientId, date, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieves a single zorgtraject by ID. User must be in same organization.
         * @summary Get zorgtraject by ID
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getZorgtrajectById(zorgtrajectId: string, options?: RawAxiosRequestConfig): AxiosPromise<ZorgtrajectResponse> {
            return localVarFp.getZorgtrajectById(zorgtrajectId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns authorization capabilities for a specific zorgtraject. This endpoint is used by the frontend to determine which actions the current user can perform on the zorgtraject. 
         * @summary Get zorgtraject capabilities
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getZorgtrajectCapabilities(zorgtrajectId: string, options?: RawAxiosRequestConfig): AxiosPromise<ZorgtrajectCapabilitiesResponse> {
            return localVarFp.getZorgtrajectCapabilities(zorgtrajectId, options).then((request) => request(axios, basePath));
        },
        /**
         * Puts one practitioner\'s session with one cliënt on a toewijzing that covers it: registers it there when the afspraak was waiting on the choice, moves its registration there when it was on another. The rules of `updateRegistratie` apply to a move, so a session in a month already declared to the gemeente cannot move until that month is credited. 
         * @summary Choose the toewijzing a session went to
         * @param {string} appointmentId Appointment UUID
         * @param {KiesToewijzingRequest} kiesToewijzingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        kiesAppointmentToewijzing(appointmentId: string, kiesToewijzingRequest: KiesToewijzingRequest, options?: RawAxiosRequestConfig): AxiosPromise<ToewijzingKeuzeResponse> {
            return localVarFp.kiesAppointmentToewijzing(appointmentId, kiesToewijzingRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * One entry per present cliënt on a Jeugdwet zorgtraject and attending practitioner of an afgeronde afspraak (#4819): the toewijzingen that name the practitioner and cover the session, and the registration it became. `open` is true when more than one covers it and nobody has chosen yet — nothing is registered until someone does. Empty for an afspraak that is not `COMPLETED` or has no Jeugdwet cliënt. 
         * @summary Which toewijzing each Jeugdwet session of an afspraak went to
         * @param {string} appointmentId Appointment UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAppointmentToewijzingKeuzes(appointmentId: string, options?: RawAxiosRequestConfig): AxiosPromise<Array<ToewijzingKeuzeResponse>> {
            return localVarFp.listAppointmentToewijzingKeuzes(appointmentId, options).then((request) => request(axios, basePath));
        },
        /**
         * List zorgtrajecten for a specific client with pagination and filtering. Results are ordered by startdatum descending (most recent first). 
         * @summary List zorgtrajecten for client
         * @param {string} clientId Client UUID
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page
         * @param {ZorgtrajectStatus} [status] Filter by status
         * @param {boolean} [includeAfgesloten] Include afgesloten (closed) zorgtrajecten
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listClientZorgtrajecten(clientId: string, page?: number, size?: number, status?: ZorgtrajectStatus, includeAfgesloten?: boolean, options?: RawAxiosRequestConfig): AxiosPromise<ZorgtrajectListResponse> {
            return localVarFp.listClientZorgtrajecten(clientId, page, size, status, includeAfgesloten, options).then((request) => request(axios, basePath));
        },
        /**
         * List all zorgtrajecten in the authenticated user\'s organization with pagination, search, and filtering capabilities. Useful for admin/overview purposes. 
         * @summary List all zorgtrajecten in organization
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page
         * @param {string} [search] Search query (searches client name)
         * @param {ZorgtrajectStatus} [status] Filter by status
         * @param {string} [regiebehandelaarId] Filter by regiebehandelaar user ID
         * @param {string} [startdatumFrom] Filter by minimum startdatum
         * @param {string} [startdatumTo] Filter by maximum startdatum
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listOrganizationZorgtrajecten(page?: number, size?: number, search?: string, status?: ZorgtrajectStatus, regiebehandelaarId?: string, startdatumFrom?: string, startdatumTo?: string, options?: RawAxiosRequestConfig): AxiosPromise<ZorgtrajectListResponse> {
            return localVarFp.listOrganizationZorgtrajecten(page, size, search, status, regiebehandelaarId, startdatumFrom, startdatumTo, options).then((request) => request(axios, basePath));
        },
        /**
         * Every registration of delivered care on the zorgtraject — with or without an appointment behind it (ADR-030 addendum A4) — most recent `datum` first. `directMinutes` is resolved: for a registration that names an appointment it is that appointment\'s duration now. 
         * @summary List the care registered on a zorgtraject
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listRegistraties(zorgtrajectId: string, options?: RawAxiosRequestConfig): AxiosPromise<Array<RegistratieResponse>> {
            return localVarFp.listRegistraties(zorgtrajectId, options).then((request) => request(axios, basePath));
        },
        /**
         * Every toewijzing the zorgtraject holds — a gemeente\'s allocation of one product each (ADR-030 addendum A1) — most recent ingangsdatum first. Empty for a zorgtraject that holds none, which includes every zorgtraject outside Jeugdwet and Wmo. 
         * @summary List a zorgtraject\'s toewijzingen
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listToewijzingen(zorgtrajectId: string, options?: RawAxiosRequestConfig): AxiosPromise<Array<ToewijzingResponse>> {
            return localVarFp.listToewijzingen(zorgtrajectId, options).then((request) => request(axios, basePath));
        },
        /**
         * Builds a new JW305 for a toewijzing whose latest start message the gemeente\'s JW306 rejected (`startberichtAfkeuring`), from the dossier as it is now, and queues it in the outbox (#4866). The practice corrects what the retourcodes name first. The start is dated as the first was: the earliest afgeronde afspraak registered against the toewijzing, never before its `ingangsdatum`. Declaring against the toewijzing stays held until a JW306 accepts this one.  Held to the zorgtraject\'s `close` capability, like the stop message: a closed zorgtraject\'s lines are held by a rejected start as much as a running one\'s. 
         * @summary Send a rejected start message again
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {string} toewijzingId Toewijzing UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        queueToewijzingStartbericht(zorgtrajectId: string, toewijzingId: string, options?: RawAxiosRequestConfig): AxiosPromise<ToewijzingResponse> {
            return localVarFp.queueToewijzingStartbericht(zorgtrajectId, toewijzingId, options).then((request) => request(axios, basePath));
        },
        /**
         * Builds a JW307 saying delivery against the toewijzing stopped on `einddatum`, for `redenBeeindiging`, and queues it in the outbox (#4816) — the stop a closed zorgtraject deferred, or one for a toewijzing whose delivery ended while the zorgtraject runs on. Clears the deferral and its signal. Only on a Jeugdwet zorgtraject, and only once per toewijzing.  Held to the zorgtraject\'s `close` capability rather than `update`: a closed zorgtraject refuses every update, and sending the stop its closing deferred is part of closing it. 
         * @summary Queue a toewijzing\'s stop message
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {string} toewijzingId Toewijzing UUID
         * @param {ToewijzingStopberichtRequest} toewijzingStopberichtRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        queueToewijzingStopbericht(zorgtrajectId: string, toewijzingId: string, toewijzingStopberichtRequest: ToewijzingStopberichtRequest, options?: RawAxiosRequestConfig): AxiosPromise<ToewijzingResponse> {
            return localVarFp.queueToewijzingStopbericht(zorgtrajectId, toewijzingId, toewijzingStopberichtRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Reopens a closed zorgtraject by clearing einddatum and setting status to ACTIEF. 
         * @summary Reopen zorgtraject
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        reopenZorgtraject(zorgtrajectId: string, options?: RawAxiosRequestConfig): AxiosPromise<ZorgtrajectResponse> {
            return localVarFp.reopenZorgtraject(zorgtrajectId, options).then((request) => request(axios, basePath));
        },
        /**
         * Replaces everything a registration says. The rules are those of `createRegistratie`.
         * @summary Change a registration
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {string} registratieId Registratie UUID
         * @param {RegistratieRequest} registratieRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateRegistratie(zorgtrajectId: string, registratieId: string, registratieRequest: RegistratieRequest, options?: RawAxiosRequestConfig): AxiosPromise<RegistratieResponse> {
            return localVarFp.updateRegistratie(zorgtrajectId, registratieId, registratieRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Replaces everything a `MANUAL` toewijzing says, including who delivers against it. A `JW301` toewijzing is the gemeente\'s message and is refused with `409`: a changed allocation arrives as a new message, and one the gemeente approved without a message is recorded with `adjustToewijzing`. 
         * @summary Change a toewijzing entered by hand
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {string} toewijzingId Toewijzing UUID
         * @param {ToewijzingRequest} toewijzingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateToewijzing(zorgtrajectId: string, toewijzingId: string, toewijzingRequest: ToewijzingRequest, options?: RawAxiosRequestConfig): AxiosPromise<ToewijzingResponse> {
            return localVarFp.updateToewijzing(zorgtrajectId, toewijzingId, toewijzingRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Updates an existing zorgtraject. User must be in same organization. Note: Setting cannot be changed after creation - must close and create new zorgtraject.  **Startdatum editing rules:** - Startdatum can only be changed if no appointments exist for this zorgtraject - When startdatum changes, it cascades to the first period of all versioned fields - Returns 409 Conflict if appointments exist and startdatum change is attempted 
         * @summary Update zorgtraject
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {UpdateZorgtrajectRequest} updateZorgtrajectRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateZorgtraject(zorgtrajectId: string, updateZorgtrajectRequest: UpdateZorgtrajectRequest, options?: RawAxiosRequestConfig): AxiosPromise<ZorgtrajectResponse> {
            return localVarFp.updateZorgtraject(zorgtrajectId, updateZorgtrajectRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ZorgtrajectenApi - object-oriented interface
 */
export class ZorgtrajectenApi extends BaseAPI {
    /**
     * Records an adjustment on top of a toewijzing — a new volume, a new einddatum, or both, from an `ingangsdatum`, with the practice\'s reason (ADR-030 V1). For a gemeente that approves an increase or an extension by email, or lets the practice push the einddatum out, and sends no JW301. Allowed on a `MANUAL` and a `JW301` toewijzing alike, to whoever may update the zorgtraject.  What the toewijzing stores stays as received; the response\'s `geldendeOmvang` and `geldendeEinddatum` are what applies now. A later JW301 for the same `toewijzingNummer` supersedes every adjustment recorded before it, as does an `updateToewijzing` that changes what a `MANUAL` toewijzing allocated. 
     * @summary Record a change the gemeente approved without a message
     * @param {string} zorgtrajectId Zorgtraject UUID
     * @param {string} toewijzingId Toewijzing UUID
     * @param {ToewijzingAanpassingRequest} toewijzingAanpassingRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public adjustToewijzing(zorgtrajectId: string, toewijzingId: string, toewijzingAanpassingRequest: ToewijzingAanpassingRequest, options?: RawAxiosRequestConfig) {
        return ZorgtrajectenApiFp(this.configuration).adjustToewijzing(zorgtrajectId, toewijzingId, toewijzingAanpassingRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Checks whether the prerequisites for creating a behandelconsult (treatment appointment) are met for this zorgtraject.  A behandelconsult requires: 1. A DSM-5 diagnosis is set on the zorgtraject 2. At least one completed diagnostic appointment with the regiebehandelaar  This endpoint allows the frontend to proactively warn the user when prerequisites are not met. The prerequisites are advisory only — neither this endpoint nor appointment creation blocks on them. 
     * @summary Check behandelconsult prerequisites
     * @param {string} zorgtrajectId Zorgtraject UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public checkBehandelconsultPrerequisites(zorgtrajectId: string, options?: RawAxiosRequestConfig) {
        return ZorgtrajectenApiFp(this.configuration).checkBehandelconsultPrerequisites(zorgtrajectId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * An aspecifieke toewijzing names only a productcategorie, and a gemeente declaration names one productcode. Where the contracts hold several products in the categorie, the practice chooses one here, once for the whole toewijzing; a null `productCode` clears the choice. Allowed on a `MANUAL`, `JW301` and `IMPORT` toewijzing alike, to whoever may update the zorgtraject (the `update` capability).  The choice is stored as `gekozenProductCode`, beside the gemeente\'s `productCode`, and is never sent to the gemeente as its own. A later JW301 keeps it, unless that JW301 names a productcode or another categorie. Months held for want of a product are declared on the next line generation; a month already declared is not declared again. 
     * @summary Choose the product an aspecifieke toewijzing is declared as
     * @param {string} zorgtrajectId Zorgtraject UUID
     * @param {string} toewijzingId Toewijzing UUID
     * @param {ToewijzingProductKeuzeRequest} toewijzingProductKeuzeRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public chooseToewijzingProduct(zorgtrajectId: string, toewijzingId: string, toewijzingProductKeuzeRequest: ToewijzingProductKeuzeRequest, options?: RawAxiosRequestConfig) {
        return ZorgtrajectenApiFp(this.configuration).chooseToewijzingProduct(zorgtrajectId, toewijzingId, toewijzingProductKeuzeRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Closes a zorgtraject by setting einddatum and status to AFGESLOTEN. Once closed, the zorgtraject cannot be modified except via reopen.  On a Jeugdwet zorgtraject, every toewijzing whose `stopbericht` is still owed — not `BEEINDIGD`, and no JW307 `KLAARGEZET` — is answered in the same call (#4816): the ones named in `stopberichten` get a JW307 queued in the outbox, and every other one is `UITGESTELD`, which raises a signal on the zorgtraject until its stop is queued from the toewijzing. A stop message is never sent that the request did not name. 
     * @summary Close zorgtraject
     * @param {string} zorgtrajectId Zorgtraject UUID
     * @param {CloseZorgtrajectRequest} closeZorgtrajectRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public closeZorgtraject(zorgtrajectId: string, closeZorgtrajectRequest: CloseZorgtrajectRequest, options?: RawAxiosRequestConfig) {
        return ZorgtrajectenApiFp(this.configuration).closeZorgtraject(zorgtrajectId, closeZorgtrajectRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Registers direct, indirect and travel minutes a medewerker delivered on the zorgtraject, to whoever may update the zorgtraject (the `update` capability). Nothing reads a registration to generate a billing line yet. 
     * @summary Register delivered care
     * @param {string} zorgtrajectId Zorgtraject UUID
     * @param {RegistratieRequest} registratieRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createRegistratie(zorgtrajectId: string, registratieRequest: RegistratieRequest, options?: RawAxiosRequestConfig) {
        return ZorgtrajectenApiFp(this.configuration).createRegistratie(zorgtrajectId, registratieRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Records a toewijzing entered by hand (`source: MANUAL`), until JW301 messages are live. Allowed only on a `JEUGDWET` or `WMO` zorgtraject, to whoever may update the zorgtraject (the `update` capability). Nothing reads a toewijzing to generate a billing line yet. 
     * @summary Record a toewijzing by hand
     * @param {string} zorgtrajectId Zorgtraject UUID
     * @param {ToewijzingRequest} toewijzingRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createToewijzing(zorgtrajectId: string, toewijzingRequest: ToewijzingRequest, options?: RawAxiosRequestConfig) {
        return ZorgtrajectenApiFp(this.configuration).createToewijzing(zorgtrajectId, toewijzingRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Creates a new zorgtraject for a client in the authenticated user\'s organization
     * @summary Create new zorgtraject for client
     * @param {string} clientId Client UUID
     * @param {CreateZorgtrajectRequest} createZorgtrajectRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createZorgtraject(clientId: string, createZorgtrajectRequest: CreateZorgtrajectRequest, options?: RawAxiosRequestConfig) {
        return ZorgtrajectenApiFp(this.configuration).createZorgtraject(clientId, createZorgtrajectRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Removes a registration that should never have been made; the audit log keeps the removal.
     * @summary Remove a registration entered in error
     * @param {string} zorgtrajectId Zorgtraject UUID
     * @param {string} registratieId Registratie UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteRegistratie(zorgtrajectId: string, registratieId: string, options?: RawAxiosRequestConfig) {
        return ZorgtrajectenApiFp(this.configuration).deleteRegistratie(zorgtrajectId, registratieId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Removes a `MANUAL` toewijzing that should never have been recorded; the audit log keeps the removal. A `JW301` toewijzing is refused with `409`. 
     * @summary Remove a toewijzing entered in error
     * @param {string} zorgtrajectId Zorgtraject UUID
     * @param {string} toewijzingId Toewijzing UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteToewijzing(zorgtrajectId: string, toewijzingId: string, options?: RawAxiosRequestConfig) {
        return ZorgtrajectenApiFp(this.configuration).deleteToewijzing(zorgtrajectId, toewijzingId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Find the active zorgtraject that should be used for an appointment on a specific date.  Selection logic: 1. Client has 1 active zorgtraject → return it 2. Client has multiple active zorgtrajecten → return them all (future: match by location) 3. Client has no active zorgtraject → return empty with warning  A zorgtraject is considered active for a date if: - Status = ACTIEF - startdatum <= date - einddatum is null OR einddatum >= date 
     * @summary Find matching zorgtraject for appointment
     * @param {string} clientId Client UUID
     * @param {string} date Date for which to find matching zorgtraject (typically appointment date)
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public findMatchingZorgtraject(clientId: string, date: string, options?: RawAxiosRequestConfig) {
        return ZorgtrajectenApiFp(this.configuration).findMatchingZorgtraject(clientId, date, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieves a single zorgtraject by ID. User must be in same organization.
     * @summary Get zorgtraject by ID
     * @param {string} zorgtrajectId Zorgtraject UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getZorgtrajectById(zorgtrajectId: string, options?: RawAxiosRequestConfig) {
        return ZorgtrajectenApiFp(this.configuration).getZorgtrajectById(zorgtrajectId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns authorization capabilities for a specific zorgtraject. This endpoint is used by the frontend to determine which actions the current user can perform on the zorgtraject. 
     * @summary Get zorgtraject capabilities
     * @param {string} zorgtrajectId Zorgtraject UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getZorgtrajectCapabilities(zorgtrajectId: string, options?: RawAxiosRequestConfig) {
        return ZorgtrajectenApiFp(this.configuration).getZorgtrajectCapabilities(zorgtrajectId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Puts one practitioner\'s session with one cliënt on a toewijzing that covers it: registers it there when the afspraak was waiting on the choice, moves its registration there when it was on another. The rules of `updateRegistratie` apply to a move, so a session in a month already declared to the gemeente cannot move until that month is credited. 
     * @summary Choose the toewijzing a session went to
     * @param {string} appointmentId Appointment UUID
     * @param {KiesToewijzingRequest} kiesToewijzingRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public kiesAppointmentToewijzing(appointmentId: string, kiesToewijzingRequest: KiesToewijzingRequest, options?: RawAxiosRequestConfig) {
        return ZorgtrajectenApiFp(this.configuration).kiesAppointmentToewijzing(appointmentId, kiesToewijzingRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * One entry per present cliënt on a Jeugdwet zorgtraject and attending practitioner of an afgeronde afspraak (#4819): the toewijzingen that name the practitioner and cover the session, and the registration it became. `open` is true when more than one covers it and nobody has chosen yet — nothing is registered until someone does. Empty for an afspraak that is not `COMPLETED` or has no Jeugdwet cliënt. 
     * @summary Which toewijzing each Jeugdwet session of an afspraak went to
     * @param {string} appointmentId Appointment UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listAppointmentToewijzingKeuzes(appointmentId: string, options?: RawAxiosRequestConfig) {
        return ZorgtrajectenApiFp(this.configuration).listAppointmentToewijzingKeuzes(appointmentId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List zorgtrajecten for a specific client with pagination and filtering. Results are ordered by startdatum descending (most recent first). 
     * @summary List zorgtrajecten for client
     * @param {string} clientId Client UUID
     * @param {number} [page] Page number (0-indexed)
     * @param {number} [size] Number of items per page
     * @param {ZorgtrajectStatus} [status] Filter by status
     * @param {boolean} [includeAfgesloten] Include afgesloten (closed) zorgtrajecten
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listClientZorgtrajecten(clientId: string, page?: number, size?: number, status?: ZorgtrajectStatus, includeAfgesloten?: boolean, options?: RawAxiosRequestConfig) {
        return ZorgtrajectenApiFp(this.configuration).listClientZorgtrajecten(clientId, page, size, status, includeAfgesloten, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List all zorgtrajecten in the authenticated user\'s organization with pagination, search, and filtering capabilities. Useful for admin/overview purposes. 
     * @summary List all zorgtrajecten in organization
     * @param {number} [page] Page number (0-indexed)
     * @param {number} [size] Number of items per page
     * @param {string} [search] Search query (searches client name)
     * @param {ZorgtrajectStatus} [status] Filter by status
     * @param {string} [regiebehandelaarId] Filter by regiebehandelaar user ID
     * @param {string} [startdatumFrom] Filter by minimum startdatum
     * @param {string} [startdatumTo] Filter by maximum startdatum
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listOrganizationZorgtrajecten(page?: number, size?: number, search?: string, status?: ZorgtrajectStatus, regiebehandelaarId?: string, startdatumFrom?: string, startdatumTo?: string, options?: RawAxiosRequestConfig) {
        return ZorgtrajectenApiFp(this.configuration).listOrganizationZorgtrajecten(page, size, search, status, regiebehandelaarId, startdatumFrom, startdatumTo, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Every registration of delivered care on the zorgtraject — with or without an appointment behind it (ADR-030 addendum A4) — most recent `datum` first. `directMinutes` is resolved: for a registration that names an appointment it is that appointment\'s duration now. 
     * @summary List the care registered on a zorgtraject
     * @param {string} zorgtrajectId Zorgtraject UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listRegistraties(zorgtrajectId: string, options?: RawAxiosRequestConfig) {
        return ZorgtrajectenApiFp(this.configuration).listRegistraties(zorgtrajectId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Every toewijzing the zorgtraject holds — a gemeente\'s allocation of one product each (ADR-030 addendum A1) — most recent ingangsdatum first. Empty for a zorgtraject that holds none, which includes every zorgtraject outside Jeugdwet and Wmo. 
     * @summary List a zorgtraject\'s toewijzingen
     * @param {string} zorgtrajectId Zorgtraject UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listToewijzingen(zorgtrajectId: string, options?: RawAxiosRequestConfig) {
        return ZorgtrajectenApiFp(this.configuration).listToewijzingen(zorgtrajectId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Builds a new JW305 for a toewijzing whose latest start message the gemeente\'s JW306 rejected (`startberichtAfkeuring`), from the dossier as it is now, and queues it in the outbox (#4866). The practice corrects what the retourcodes name first. The start is dated as the first was: the earliest afgeronde afspraak registered against the toewijzing, never before its `ingangsdatum`. Declaring against the toewijzing stays held until a JW306 accepts this one.  Held to the zorgtraject\'s `close` capability, like the stop message: a closed zorgtraject\'s lines are held by a rejected start as much as a running one\'s. 
     * @summary Send a rejected start message again
     * @param {string} zorgtrajectId Zorgtraject UUID
     * @param {string} toewijzingId Toewijzing UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public queueToewijzingStartbericht(zorgtrajectId: string, toewijzingId: string, options?: RawAxiosRequestConfig) {
        return ZorgtrajectenApiFp(this.configuration).queueToewijzingStartbericht(zorgtrajectId, toewijzingId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Builds a JW307 saying delivery against the toewijzing stopped on `einddatum`, for `redenBeeindiging`, and queues it in the outbox (#4816) — the stop a closed zorgtraject deferred, or one for a toewijzing whose delivery ended while the zorgtraject runs on. Clears the deferral and its signal. Only on a Jeugdwet zorgtraject, and only once per toewijzing.  Held to the zorgtraject\'s `close` capability rather than `update`: a closed zorgtraject refuses every update, and sending the stop its closing deferred is part of closing it. 
     * @summary Queue a toewijzing\'s stop message
     * @param {string} zorgtrajectId Zorgtraject UUID
     * @param {string} toewijzingId Toewijzing UUID
     * @param {ToewijzingStopberichtRequest} toewijzingStopberichtRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public queueToewijzingStopbericht(zorgtrajectId: string, toewijzingId: string, toewijzingStopberichtRequest: ToewijzingStopberichtRequest, options?: RawAxiosRequestConfig) {
        return ZorgtrajectenApiFp(this.configuration).queueToewijzingStopbericht(zorgtrajectId, toewijzingId, toewijzingStopberichtRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Reopens a closed zorgtraject by clearing einddatum and setting status to ACTIEF. 
     * @summary Reopen zorgtraject
     * @param {string} zorgtrajectId Zorgtraject UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public reopenZorgtraject(zorgtrajectId: string, options?: RawAxiosRequestConfig) {
        return ZorgtrajectenApiFp(this.configuration).reopenZorgtraject(zorgtrajectId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Replaces everything a registration says. The rules are those of `createRegistratie`.
     * @summary Change a registration
     * @param {string} zorgtrajectId Zorgtraject UUID
     * @param {string} registratieId Registratie UUID
     * @param {RegistratieRequest} registratieRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateRegistratie(zorgtrajectId: string, registratieId: string, registratieRequest: RegistratieRequest, options?: RawAxiosRequestConfig) {
        return ZorgtrajectenApiFp(this.configuration).updateRegistratie(zorgtrajectId, registratieId, registratieRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Replaces everything a `MANUAL` toewijzing says, including who delivers against it. A `JW301` toewijzing is the gemeente\'s message and is refused with `409`: a changed allocation arrives as a new message, and one the gemeente approved without a message is recorded with `adjustToewijzing`. 
     * @summary Change a toewijzing entered by hand
     * @param {string} zorgtrajectId Zorgtraject UUID
     * @param {string} toewijzingId Toewijzing UUID
     * @param {ToewijzingRequest} toewijzingRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateToewijzing(zorgtrajectId: string, toewijzingId: string, toewijzingRequest: ToewijzingRequest, options?: RawAxiosRequestConfig) {
        return ZorgtrajectenApiFp(this.configuration).updateToewijzing(zorgtrajectId, toewijzingId, toewijzingRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Updates an existing zorgtraject. User must be in same organization. Note: Setting cannot be changed after creation - must close and create new zorgtraject.  **Startdatum editing rules:** - Startdatum can only be changed if no appointments exist for this zorgtraject - When startdatum changes, it cascades to the first period of all versioned fields - Returns 409 Conflict if appointments exist and startdatum change is attempted 
     * @summary Update zorgtraject
     * @param {string} zorgtrajectId Zorgtraject UUID
     * @param {UpdateZorgtrajectRequest} updateZorgtrajectRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateZorgtraject(zorgtrajectId: string, updateZorgtrajectRequest: UpdateZorgtrajectRequest, options?: RawAxiosRequestConfig) {
        return ZorgtrajectenApiFp(this.configuration).updateZorgtraject(zorgtrajectId, updateZorgtrajectRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



